let activeObserver = null;
let currentOwner = null;
let batchDepth = 0;
let flushQueued = false;
const pending = new Set();
let devtoolsConnection = null;
let __noxidDevtoolsHooks = null;
let runtimeIdSequence = 0;
let transactionSequence = 0;
let currentTransaction = null;
let maxObserverRunsPerFlush = 100;
const scenarioHarnessToken = Symbol("noxid.scenario-harness");

function isScenarioStub(options, semanticId) {
  return options?.scenarioHarnessToken === scenarioHarnessToken
    && options?.scenarioStubs?.has?.(semanticId) === true;
}

function nextRuntimeId(kind) {
  runtimeIdSequence += 1;
  return `${kind}:${runtimeIdSequence}`;
}

export function configureScheduler(options = {}) {
  if (options.maxObserverRunsPerFlush !== undefined) {
    const value = options.maxObserverRunsPerFlush;
    if (!Number.isInteger(value) || value < 1) {
      throw new RangeError("maxObserverRunsPerFlush must be a positive integer");
    }
    maxObserverRunsPerFlush = value;
  }
  return Object.freeze({ maxObserverRunsPerFlush });
}

export class ReactiveFlushError extends Error {
  constructor(observer, runs) {
    const identity = observer.semanticId ?? observer.runtimeId ?? observer.kind ?? "anonymous observer";
    super(`Reactive scheduler stopped a probable update loop in ${identity} after ${runs} runs`);
    this.name = "ReactiveFlushError";
    this.code = "REACTIVE_FLUSH_LIMIT";
    this.semanticId = observer.semanticId ?? null;
    this.runtimeId = observer.runtimeId ?? null;
    this.ownerId = observer.ownerId ?? null;
    this.observerKind = observer.kind ?? null;
    this.runs = runs;
    this.limit = maxObserverRunsPerFlush;
  }
}


function devtoolsValueType(value) {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
}

function emitDevtools(kind, details = {}) {
  if (devtoolsConnection) __noxidDevtoolsHooks.emit(kind, details);
}

function registerDevtoolsDom(node, identity, owner = null) {
  __noxidDevtoolsHooks?.registerDom(node, identity, owner);
}


// WO-56. The one place an author-written `log` statement reaches the browser.
// The runtime itself never calls it: only compiler-emitted call sites do, and
// their level, message, semantic id, and field names are compile-time
// constants, so the console and the DevTools timeline see exactly the sentence
// and the named scalars the source declared.
const NOXID_LOG_SCHEMA = "noxid.log.v1";
const NOXID_LOG_SEVERITY = Object.freeze({ debug: 0, info: 1, warn: 2, error: 3 });
const NOXID_LOG_CONSOLE_METHOD = Object.freeze({ debug: "debug", info: "info", warn: "warn", error: "error" });
const NOXID_LOG_STRING_LIMIT = 512;
const NOXID_LOG_BUFFER_LIMIT = 500;
const noxidLogBuffer = [];
// Replaced at build time by the compiler with the console threshold this
// build was configured for: 0 debug, 1 info, 2 warn, 3 error, 4 silent.
let noxidLogConsoleThreshold = 2;

function noxidLogScalar(value) {
  if (typeof value === "string") {
    return value.length > NOXID_LOG_STRING_LIMIT ? `${value.slice(0, NOXID_LOG_STRING_LIMIT)}…` : value;
  }
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

export function noxidLog(level, message, semanticId, fields = {}) {
  const severity = NOXID_LOG_SEVERITY[level];
  if (severity === undefined) return null;
  const values = Object.create(null);
  for (const key of Object.keys(fields)) {
    const value = noxidLogScalar(fields[key]);
    if (value !== null) values[key] = value;
  }
  const record = Object.freeze({
    schema: NOXID_LOG_SCHEMA,
    level,
    message,
    semanticId,
    fields: Object.freeze(values),
  });
  if (noxidLogBuffer.length >= NOXID_LOG_BUFFER_LIMIT) noxidLogBuffer.shift();
  noxidLogBuffer.push(record);
  emitDevtools("log.emitted", { semanticId, payload: { level, message, fields: values } });
  if (severity >= noxidLogConsoleThreshold) {
    try {
      console?.[NOXID_LOG_CONSOLE_METHOD[level]]?.(record);
    } catch {}
  }
  return record;
}

export function noxidLogRecords() {
  return Object.freeze([...noxidLogBuffer]);
}

export function noxidClearLogRecords() {
  noxidLogBuffer.splice(0);
}

export function emitRuntimeEvent(kind, details = {}) {
  if (typeof kind !== "string" || kind.length === 0) {
    throw new TypeError("Runtime event kind must be a non-empty string");
  }
  emitDevtools(kind, details);
}

function cleanupDependencies(observer) {
  for (const dependency of observer.dependencies) dependency.delete(observer);
  observer.dependencies.clear();
}

function track(subscribers) {
  if (activeObserver && !subscribers.has(activeObserver)) {
    subscribers.add(activeObserver);
    activeObserver.dependencies.add(subscribers);
  }
}

function enqueue(observer) {
  if (observer.disposed) return;
  pending.add(observer);
  if (batchDepth === 0 && !flushQueued) {
    flushQueued = true;
    queueMicrotask(flush);
  }
}

export function flush() {
  flushQueued = false;
  const runs = new Map();
  try {
    while (pending.size) {
      const work = [...pending];
      pending.clear();
      for (const observer of work) {
        const count = (runs.get(observer) ?? 0) + 1;
        runs.set(observer, count);
        if (count > maxObserverRunsPerFlush) {
          const error = new ReactiveFlushError(observer, count);
          emitDevtools("scheduler.loop.detected", {
            semanticId: error.semanticId,
            runtimeId: error.runtimeId,
            ownerId: error.ownerId,
            payload: { code: error.code, runs: count, limit: error.limit, observerKind: error.observerKind },
          });
          throw error;
        }
        observer.run();
      }
    }
  } catch (error) {
    pending.clear();
    throw error;
  }
}

export function batch(fn) {
  batchDepth += 1;
  try { return fn(); }
  finally {
    batchDepth -= 1;
    if (batchDepth === 0) flush();
  }
}

function publishTransactionChanges(transaction) {
  for (const change of transaction.changes.values()) {
    emitDevtools("signal.changed", {
      semanticId: change.semanticId,
      runtimeId: change.runtimeId,
      ownerId: change.ownerId,
      transactionId: transaction.id,
      capture: change.capture,
      payload: { previous: change.previous, value: change.value },
    });
  }
  __noxidDevtoolsHooks?.recordTransaction(transaction);
}

export function runAction(semanticId, owner, fn) {
  const runtimeId = nextRuntimeId("action");
  const parentTransaction = currentTransaction;
  const ownsTransaction = parentTransaction == null;
  const transaction = parentTransaction ?? {
    id: `transaction:${++transactionSequence}`,
    action: semanticId,
    changes: new Map(),
  };
  currentTransaction = transaction;
  if (ownsTransaction) {
    emitDevtools("transaction.started", {
      semanticId,
      runtimeId: transaction.id,
      ownerId: owner?.id ?? null,
      transactionId: transaction.id,
    });
  }
  emitDevtools("action.started", {
    semanticId,
    runtimeId,
    ownerId: owner?.id ?? null,
    transactionId: transaction.id,
  });
  try {
    const result = batch(fn);
    emitDevtools("action.completed", {
      semanticId,
      runtimeId,
      ownerId: owner?.id ?? null,
      transactionId: transaction.id,
    });
    if (ownsTransaction) {
      publishTransactionChanges(transaction);
      emitDevtools("transaction.committed", {
        semanticId,
        runtimeId: transaction.id,
        ownerId: owner?.id ?? null,
        transactionId: transaction.id,
        payload: { changedSignals: transaction.changes.size },
      });
    }
    return result;
  } catch (error) {
    emitDevtools("action.failed", {
      semanticId,
      runtimeId,
      ownerId: owner?.id ?? null,
      transactionId: transaction.id,
      payload: { code: error?.code ?? null, message: error?.message ?? String(error) },
    });
    if (ownsTransaction) {
      publishTransactionChanges(transaction);
      emitDevtools("transaction.failed", {
        semanticId,
        runtimeId: transaction.id,
        ownerId: owner?.id ?? null,
        transactionId: transaction.id,
        payload: { changedSignals: transaction.changes.size },
      });
    }
    throw error;
  } finally {
    currentTransaction = parentTransaction;
  }
}

export function signal(initialValue, metadata = null) {
  let value = initialValue;
  const subscribers = new Set();
  const semanticId = typeof metadata === "string" ? metadata : metadata?.semanticId ?? null;
  const owner = typeof metadata === "object" ? metadata?.owner ?? currentOwner : currentOwner;
  const capture = typeof metadata === "object" ? metadata?.capture !== false : true;
  const type = typeof metadata === "object" ? metadata?.type ?? devtoolsValueType(initialValue) : devtoolsValueType(initialValue);
  const validatorId = typeof metadata === "object" ? metadata?.validatorId ?? null : null;
  const validator = typeof metadata === "object" && typeof metadata?.validator === "function" ? metadata.validator : null;
  const runtimeId = nextRuntimeId("signal");
  const source = {
    semanticId,
    runtimeId,
    capture,
    ownerId: owner?.id ?? null,
    get() { track(subscribers); return value; },
    set(nextValue) {
      if (Object.is(value, nextValue)) return;
      const previous = value;
      value = nextValue;
      for (const observer of [...subscribers]) observer.schedule();
      if (currentTransaction && devtoolsConnection) {
        const existing = currentTransaction.changes.get(runtimeId);
        currentTransaction.changes.set(runtimeId, {
          semanticId,
          runtimeId,
          ownerId: owner?.id ?? null,
          capture,
          previous: existing?.previous ?? previous,
          value: nextValue,
        });
      } else if (devtoolsConnection) {
        emitDevtools("signal.changed", {
          semanticId,
          runtimeId,
          ownerId: owner?.id ?? null,
          capture,
          payload: { previous, value: nextValue },
        });
      }
    },
    subscribe(observer) { subscribers.add(observer); observer.dependencies.add(subscribers); }
  };
  if (__noxidDevtoolsHooks) __noxidDevtoolsHooks.registerSignal(runtimeId, { semanticId, type, validatorId, validator, initialValue, source });
  emitDevtools("signal.created", {
    semanticId,
    runtimeId,
    ownerId: owner?.id ?? null,
    capture,
    payload: { value: initialValue },
  });
  if (owner) onCleanup(owner, () => { __noxidDevtoolsHooks?.releaseSignal(runtimeId); emitDevtools("signal.disposed", { semanticId, runtimeId, ownerId: owner.id }); });
  return source;
}

// Collection mutations follow the same recursive value equality as Noxid ==.
// Keep this inside the feature so programs without collections retain no helper.
function collectionValueEqual(left, right) {
  if (left === right) return true;
  if (Array.isArray(left) || Array.isArray(right)) return Array.isArray(left) && Array.isArray(right) && left.length === right.length && left.every((value, index) => collectionValueEqual(value, right[index]));
  if (left === null || right === null || typeof left !== "object" || typeof right !== "object") return false;
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return leftKeys.length === rightKeys.length && leftKeys.every((key, index) => key === rightKeys[index] && collectionValueEqual(left[key], right[key]));
}

export function collectionSignal(initialValue, metadata = null) {
  if (!Array.isArray(initialValue)) throw new TypeError("collectionSignal requires an array");
  const source = signal(initialValue.slice(), metadata);
  const operationSubscribers = new Set();
  function publish(operation) {
    for (const subscriber of [...operationSubscribers]) subscriber.schedule(operation);
    emitDevtools("collection.operation", {
      semanticId: source.semanticId,
      runtimeId: source.runtimeId,
      ownerId: source.ownerId,
      transactionId: currentTransaction?.id ?? null,
      capture: source.capture,
      payload: operation,
    });
  }
  return {
    semanticId: source.semanticId,
    runtimeId: source.runtimeId,
    capture: source.capture,
    ownerId: source.ownerId,
    get: source.get,
    subscribe: source.subscribe,
    subscribeOperations(subscriber) {
      operationSubscribers.add(subscriber);
      return () => operationSubscribers.delete(subscriber);
    },
    set(nextValue) {
      if (!Array.isArray(nextValue)) throw new TypeError("collection state requires an array");
      const next = nextValue.slice();
      source.set(next);
      publish({ kind: "reset", values: next });
    },
    push(value) {
      const next = source.get().slice();
      const index = next.length;
      next.push(value);
      source.set(next);
      publish({ kind: "insert", index, value });
    },
    remove(value) {
      const next = source.get().slice();
      const index = next.findIndex((candidate) => collectionValueEqual(candidate, value));
      if (index < 0) return false;
      const [removed] = next.splice(index, 1);
      source.set(next);
      publish({ kind: "delete", index, value: removed });
      return true;
    },
    removeWhere(field, value) {
      const next = source.get().slice();
      const index = next.findIndex((candidate) => collectionValueEqual(candidate?.[field], value));
      if (index < 0) return false;
      const [removed] = next.splice(index, 1);
      source.set(next);
      publish({ kind: "delete", index, value: removed });
      return true;
    },
    move(from, to) {
      const next = source.get().slice();
      if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < 0 || from >= next.length || to >= next.length) {
        const error = new RangeError(`Invalid collection move: ${from} -> ${to}`);
        error.code = "INVALID_COLLECTION_INDEX";
        error.from = from;
        error.to = to;
        error.length = next.length;
        throw error;
      }
      if (from === to) return;
      const [value] = next.splice(from, 1);
      next.splice(to, 0, value);
      source.set(next);
      publish({ kind: "move", from, to, value });
    }
  };
}

export function toSource(value) {
  if (value && typeof value.get === "function" && typeof value.subscribe === "function") return value;
  return { get() { return value; }, subscribe() {} };
}

export function propSource(props, name, component) {
  if (!Object.prototype.hasOwnProperty.call(props, name)) {
    throw new Error(`Missing required prop ${component}.${name}`);
  }
  return toSource(props[name]);
}

export function effect(fn, owner = currentOwner, sources = null, metadata = null) {
  const kind = metadata?.kind ?? "effect";
  const semanticId = typeof metadata === "string" ? metadata : metadata?.semanticId ?? null;
  const capture = typeof metadata === "object" ? metadata?.capture !== false : true;
  const runtimeId = nextRuntimeId(kind);
  const observer = {
    semanticId, runtimeId, ownerId: owner?.id ?? null, kind,
    dependencies: new Set(), disposed: false,
    schedule() {
      if (owner?.disposed) return;
      if (devtoolsConnection) emitDevtools(`${kind}.scheduled`, { semanticId, runtimeId, ownerId: owner?.id ?? null });
      enqueue(observer);
    },
    run() {
      if (observer.disposed) return;
      if (!sources) cleanupDependencies(observer);
      const previous = activeObserver;
      activeObserver = sources ? null : observer;
      if (devtoolsConnection) emitDevtools(`${kind}.running`, { semanticId, runtimeId, ownerId: owner?.id ?? null });
      try {
        fn();
        if (devtoolsConnection) emitDevtools(`${kind}.completed`, { semanticId, runtimeId, ownerId: owner?.id ?? null });
      } finally { activeObserver = previous; }
    },
    dispose() {
      if (observer.disposed) return;
      observer.disposed = true;
      pending.delete(observer);
      cleanupDependencies(observer);
      emitDevtools(`${kind}.disposed`, { semanticId, runtimeId, ownerId: owner?.id ?? null });
    }
  };
  if (sources) for (const source of sources) source.subscribe(observer);
  if (owner) owner.cleanups.push(() => observer.dispose());
  emitDevtools(`${kind}.created`, {
    semanticId,
    runtimeId,
    ownerId: owner?.id ?? null,
    capture,
    payload: { dependencyCount: sources?.length ?? null, dynamicDependencies: sources == null },
  });
  observer.run();
  return () => observer.dispose();
}

export function watch(read, callback, owner = currentOwner, sources = null, metadata = null) {
  let initialized = false;
  let previous;
  return effect(() => {
    const current = read();
    if (!initialized) {
      initialized = true;
      previous = current;
      return;
    }
    if (Object.is(current, previous)) return;
    const before = previous;
    previous = current;
    emitDevtools("watch.changed", {
      semanticId: typeof metadata === "string" ? metadata : metadata?.semanticId ?? null,
      ownerId: owner?.id ?? null,
      capture: typeof metadata === "object" ? metadata?.capture !== false : true,
      payload: { previous: before, value: current },
    });
    batch(() => callback(current, before));
  }, owner, sources, {
    ...(typeof metadata === "object" && metadata ? metadata : {}),
    semanticId: typeof metadata === "string" ? metadata : metadata?.semanticId ?? null,
    kind: "watch",
  });
}

export function computed(read, owner = currentOwner, sources = null, metadata = null) {
  let value;
  let dirty = true;
  const subscribers = new Set();
  const semanticId = typeof metadata === "string" ? metadata : metadata?.semanticId ?? null;
  const capture = typeof metadata === "object" ? metadata?.capture !== false : true;
  const runtimeId = nextRuntimeId("computed");
  const observer = {
    semanticId, runtimeId, ownerId: owner?.id ?? null, kind: "computed",
    dependencies: new Set(), disposed: false,
    schedule() {
      if (dirty || observer.disposed || owner?.disposed) return;
      dirty = true;
      emitDevtools("computed.invalidated", { semanticId, runtimeId, ownerId: owner?.id ?? null });
      for (const subscriber of [...subscribers]) subscriber.schedule();
    },
    run() {
      if (!sources) cleanupDependencies(observer);
      const previous = activeObserver;
      activeObserver = sources ? null : observer;
      try {
        value = read();
        dirty = false;
        emitDevtools("computed.evaluated", {
          semanticId,
          runtimeId,
          ownerId: owner?.id ?? null,
          capture,
          payload: { value },
        });
      } finally { activeObserver = previous; }
    },
    dispose() {
      if (observer.disposed) return;
      observer.disposed = true;
      cleanupDependencies(observer);
      subscribers.clear();
      emitDevtools("computed.disposed", { semanticId, runtimeId, ownerId: owner?.id ?? null });
    }
  };
  if (sources) for (const source of sources) source.subscribe(observer);
  if (owner) owner.cleanups.push(() => observer.dispose());
  emitDevtools("computed.created", {
    semanticId,
    runtimeId,
    ownerId: owner?.id ?? null,
    payload: { dependencyCount: sources?.length ?? null, dynamicDependencies: sources == null },
  });
  return {
    semanticId,
    runtimeId,
    get() { track(subscribers); if (dirty) observer.run(); return value; },
    subscribe(subscriber) { subscribers.add(subscriber); subscriber.dependencies.add(subscribers); }
  };
}

export function createOwner(parent = currentOwner, metadata = null) {
  const owner = {
    id: nextRuntimeId("owner"),
    semanticId: typeof metadata === "string" ? metadata : metadata?.semanticId ?? null,
    label: typeof metadata === "object" ? metadata?.label ?? null : null,
    parent,
    children: new Set(),
    contexts: new Map(),
    cleanups: [],
    disposed: false,
  };
  if (parent) parent.children.add(owner);
  __noxidDevtoolsHooks?.registerOwner(owner);
  emitDevtools("owner.created", {
    semanticId: owner.semanticId,
    runtimeId: owner.id,
    ownerId: owner.id,
    payload: { parentOwnerId: parent?.id ?? null, label: owner.label },
  });
  return owner;
}



export function withOwner(owner, fn) {
  const previous = currentOwner;
  currentOwner = owner;
  try { return fn(); } finally { currentOwner = previous; }
}

export function onCleanup(owner, cleanup) {
  if (owner.disposed) cleanup(); else owner.cleanups.push(cleanup);
}

export function registerLifecycle(owner, semanticId, mount = null, cleanup = null) {
  if (!owner || owner.disposed) throw new Error(`Cannot register disposed lifecycle ${semanticId}`);
  if (mount !== null && typeof mount !== "function") throw new TypeError("Lifecycle mount must be a function");
  if (cleanup !== null && typeof cleanup !== "function") throw new TypeError("Lifecycle cleanup must be a function");
  const runtimeId = nextRuntimeId("lifecycle");
  let cleaned = false;
  onCleanup(owner, () => {
    if (cleaned) return;
    cleaned = true;
    emitDevtools("lifecycle.cleanup.started", { semanticId, runtimeId, ownerId: owner.id });
    try {
      if (cleanup) cleanup();
      emitDevtools("lifecycle.cleanup.completed", { semanticId, runtimeId, ownerId: owner.id });
    } catch (error) {
      emitDevtools("lifecycle.cleanup.failed", {
        semanticId, runtimeId, ownerId: owner.id,
        payload: { code: error?.code ?? null, message: error?.message ?? String(error) },
      });
      throw error;
    }
  });
  emitDevtools("lifecycle.mount.started", { semanticId, runtimeId, ownerId: owner.id });
  try {
    if (mount) mount();
    emitDevtools("lifecycle.mount.completed", { semanticId, runtimeId, ownerId: owner.id });
  } catch (error) {
    emitDevtools("lifecycle.mount.failed", {
      semanticId, runtimeId, ownerId: owner.id,
      payload: { code: error?.code ?? null, message: error?.message ?? String(error) },
    });
    throw error;
  }
  return Object.freeze({ semanticId, runtimeId, owner });
}

export function disposeOwner(owner) {
  if (owner.disposed) return;
  owner.disposed = true;
  emitDevtools("owner.disposing", {
    semanticId: owner.semanticId,
    runtimeId: owner.id,
    ownerId: owner.id,
    payload: { childCount: owner.children.size, cleanupCount: owner.cleanups.length },
  });
  const failures = [];
  for (const child of [...owner.children]) {
    try { disposeOwner(child); } catch (error) { failures.push(error); }
  }
  for (const cleanup of owner.cleanups.splice(0).reverse()) {
    try { cleanup(); } catch (error) { failures.push(error); }
  }
  if (owner.parent) owner.parent.children.delete(owner);
  __noxidDevtoolsHooks?.releaseOwner(owner);
  emitDevtools("owner.disposed", { semanticId: owner.semanticId, runtimeId: owner.id, ownerId: owner.id });
  if (failures.length === 1) throw failures[0];
  if (failures.length > 1) throw new AggregateError(failures, `Owner ${owner.id} cleanup failed`);
}

function stableAcquisitionKey(value, seen = new WeakSet()) {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (seen.has(value)) throw new TypeError("Acquisition parameters cannot contain cycles");
  seen.add(value);
  let key;
  if (Array.isArray(value)) {
    key = `[${value.map((item) => stableAcquisitionKey(item, seen)).join(",")}]`;
  } else {
    const fields = Object.keys(value).sort().map((name) => `${JSON.stringify(name)}:${stableAcquisitionKey(value[name], seen)}`);
    key = `{${fields.join(",")}}`;
  }
  seen.delete(value);
  return key;
}

export function findMarker(root, marker) {
  // Every generated component numbers its markers locally. Once a child has
  // mounted (or before it hydrates), its DOM must not satisfy a lookup in the
  // enclosing template, even if both templates use the same ordinal.
  // Components reset the marker counter, and slots render another component's
  // authored scope. Conditional/match/for/stream templates instead share their
  // component's counter; repeated rows are looked up in their own fragments.
  // Only component and slot ranges therefore change marker ownership.
  const regionKind = (node) => {
    if (node.nodeType !== 8) return null;
    const match = /^noxid-(component)-\d+$/.exec(node.data)
      ?? /^noxid-(slot)-(?:\d+|[A-Za-z0-9_]+-\d+)$/.exec(node.data);
    // A slot literally named "end" has a start spelling that resembles a
    // default-slot end. Its paired end (or mounted range) disambiguates it.
    if (match?.[1] === "slot" && /^noxid-slot-end-\d+$/.test(node.data)) {
      if (node.__noxidTemplateEnd?.parentNode === node.parentNode) return "slot";
      const expected = node.data.replace("noxid-slot-", "noxid-slot-end-");
      for (let sibling = node.nextSibling; sibling; sibling = sibling.nextSibling) {
        if (sibling.nodeType === 8 && sibling.data === expected) return "slot";
      }
      return null;
    }
    return match?.[1] ?? null;
  };
  const regionEnd = (start, kind) => {
    let nested = 0;
    const mountedEnd = start.__noxidTemplateEnd;
    if (mountedEnd?.parentNode && mountedEnd.parentNode === start.parentNode) return mountedEnd;
    const expected = start.data.replace(`noxid-${kind}-`, `noxid-${kind}-end-`);
    for (let node = start.nextSibling; node; node = node.nextSibling) {
      if (regionKind(node) === kind) nested += 1;
      else if (node.nodeType === 8 && (
        node.data === `noxid-${kind}-end`
        || node.data.startsWith(`noxid-${kind}-end-`)
      )) {
        if (nested > 0) nested -= 1;
        else if (node.data === expected || node.data === `noxid-${kind}-end`) return node;
      }
    }
    // An unmounted placeholder has no range to skip yet.
    return null;
  };
  const visit = (parent) => {
    for (let node = parent.firstChild; node; node = node.nextSibling) {
      if (node.nodeType === 8 && node.data === marker) return node;
      if (node.nodeType === 1 && marker.startsWith("data-noxid-") && node.getAttribute(marker) !== null) return node;
      const kind = regionKind(node);
      const end = kind && regionEnd(node, kind);
      if (end) { node = end; continue; }
      const found = visit(node);
      if (found) return found;
    }
    return null;
  };
  const found = visit(root);
  if (found) return found;
  throw new Error(`Missing compiler DOM marker: ${marker}`);
}

function devtoolsIdentity(kind, metadata, owner) {
  return {
    semanticId: typeof metadata === "string" ? metadata : metadata?.semanticId ?? null,
    runtimeId: nextRuntimeId(kind),
    ownerId: owner?.id ?? null,
    capture: typeof metadata === "object" ? metadata?.capture !== false : true,
  };
}

export function bindText(marker, read, owner, sources = null, metadata = null) {
  const node = document.createTextNode("");
  const identity = devtoolsIdentity("dom-binding", metadata, owner);
  marker.parentNode.insertBefore(node, marker.nextSibling);
  registerDevtoolsDom(node, identity, owner);
  emitDevtools("dom.binding.created", { ...identity, payload: { kind: "text" } });
  effect(() => {
    const value = String(read());
    node.data = value;
    emitDevtools("dom.binding.updated", { ...identity, payload: { kind: "text", value } });
  }, owner, sources);
  onCleanup(owner, () => emitDevtools("dom.binding.disposed", identity));
  return node;
}

export function hydrateText(marker, read, owner, sources = null, metadata = null) {
  const identity = devtoolsIdentity("dom-binding", metadata, owner);
  const ordinal = marker.data.slice("noxid-text-".length);
  const expectedEnd = `noxid-text-end-${ordinal}`;
  let node = marker.nextSibling;
  if (node?.nodeType === Node.COMMENT_NODE && node.data === expectedEnd) {
    node = document.createTextNode("");
    marker.parentNode.insertBefore(node, marker.nextSibling);
  } else if (!(node instanceof Text) || node.nextSibling?.nodeType !== Node.COMMENT_NODE || node.nextSibling.data !== expectedEnd) {
    throw new Error(`Invalid hydrated text region: ${identity.semanticId ?? ordinal}`);
  }
  registerDevtoolsDom(node, identity, owner);
  emitDevtools("dom.binding.hydrated", { ...identity, payload: { kind: "text" } });
  effect(() => {
    const value = String(read());
    if (node.data !== value) node.data = value;
    emitDevtools("dom.binding.updated", { ...identity, payload: { kind: "text", value } });
  }, owner, sources);
  onCleanup(owner, () => emitDevtools("dom.binding.disposed", identity));
  return node;
}

export function bindAttribute(node, name, read, owner, sources = null, metadata = null) {
  const identity = devtoolsIdentity("dom-binding", metadata, owner);
  registerDevtoolsDom(node, identity, owner);
  emitDevtools("dom.binding.created", { ...identity, payload: { kind: "attribute", name } });
  effect(() => {
    const value = read();
    if (node.localName === "form" && name === "valid") {
      node.__noxidFormValid = value;
      return;
    }
    if (value === false || value == null) node.removeAttribute(name);
    else node.setAttribute(name, value === true ? "" : String(value));
    emitDevtools("dom.binding.updated", { ...identity, payload: { kind: "attribute", name, value } });
  }, owner, sources);
  onCleanup(owner, () => emitDevtools("dom.binding.disposed", identity));
}

export function bindProperty(node, name, source, owner, metadata = null) {
  if (!source || typeof source.get !== "function" || typeof source.set !== "function") {
    throw new TypeError(`Two-way binding ${name} requires a writable source`);
  }
  const identity = devtoolsIdentity("dom-binding", metadata, owner);
  registerDevtoolsDom(node, identity, owner);
  const event = name === "value" ? "input" : "change";
  emitDevtools("dom.binding.created", { ...identity, payload: { kind: "property", name, event } });
  effect(() => {
    const value = source.get();
    if (!Object.is(node[name], value)) {
      let form = node.form ?? node.parentNode;
      while (form && form.localName !== "form") form = form.parentNode;
      form?.__noxidFormEdit?.();
      node[name] = value;
    }
    emitDevtools("dom.binding.updated", { ...identity, payload: { kind: "property", name, direction: "model-to-dom", value } });
  }, owner, [source]);
  const write = () => {
    source.set(node[name]);
    emitDevtools("dom.binding.updated", { ...identity, payload: { kind: "property", name, direction: "dom-to-model", value: node[name] } });
  };
  node.addEventListener(event, write);
  onCleanup(owner, () => {
    node.removeEventListener(event, write);
    emitDevtools("dom.binding.disposed", identity);
  });
}


export function snapshotBoundaryValue(value) {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return Object.freeze(value.map(snapshotBoundaryValue));
  return Object.freeze(Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, snapshotBoundaryValue(entry)])));
}


export function listen(node, event, handler, owner, metadata = null) {
  const identity = devtoolsIdentity("dom-listener", metadata, owner);
  registerDevtoolsDom(node, identity, owner);
  const invoke = (value) => {
    emitDevtools("dom.listener.invoked", { ...identity, capture: false, payload: { event } });
    if (event === "scroll") {
      const finite = (number) => typeof number === "number" && Number.isFinite(number) ? number : 0;
      const dimension = (number) => Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, Math.trunc(finite(number))));
      return handler(Object.freeze({
        type: "scroll",
        scrollTop: finite(node.scrollTop),
        scrollLeft: finite(node.scrollLeft),
        clientHeight: dimension(node.clientHeight),
        clientWidth: dimension(node.clientWidth),
      }));
    }
    return handler(value);
  };
  node.addEventListener(event, invoke);
  emitDevtools("dom.listener.created", { ...identity, payload: { event } });
  onCleanup(owner, () => {
    node.removeEventListener(event, invoke);
    emitDevtools("dom.listener.disposed", identity);
  });
}

export function listenSubmit(node, event, handler, owner, metadata = null) {
  if (node?.localName !== "form" || event !== "submit") {
    throw new TypeError("SUBMIT_REQUIRES_FORM: handled submission requires a form");
  }
  const identity = devtoolsIdentity("form-submit", metadata, owner);
  registerDevtoolsDom(node, identity, owner);
  let pending = false;
  const invoke = (value) => {
    if (value.target && value.target !== node) return;
    value.preventDefault();
    if (owner.disposed || pending) return;
    pending = true;
    const payload = Object.freeze({
      type: "submit",
      submitterName: String(value.submitter?.name ?? ""),
      submitterValue: String(value.submitter?.value ?? ""),
    });
    emitDevtools("dom.listener.invoked", { ...identity, capture: false, payload: { event } });
    let result;
    try { result = handler(payload); }
    catch (error) { pending = false; throw error; }
    if (result && typeof result.then === "function") {
      Promise.resolve(result).then(
        () => { pending = false; },
        (error) => {
          pending = false;
          if (owner.disposed) return;
          if (typeof globalThis.reportError === "function") globalThis.reportError(error);
          else console.error("Noxid form submission failed", error);
        },
      );
    } else pending = false;
  };
  node.addEventListener("submit", invoke);
  emitDevtools("dom.listener.created", { ...identity, payload: { event } });
  onCleanup(owner, () => {
    node.removeEventListener("submit", invoke);
    emitDevtools("dom.listener.disposed", identity);
  });
}


export function componentEmitter(handlers, component) {
  return (name, payload) => {
    const handler = handlers[name];
    if (handler == null) return;
    if (typeof handler !== "function") {
      throw new TypeError(`Invalid handler for ${component}.${name}`);
    }
    handler(payload);
  };
}

function removeOwnedRange(marker, end) {
  let node = marker.nextSibling;
  while (node) {
    const next = node.nextSibling;
    node.remove();
    if (node === end) break;
    node = next;
  }
}

function removeInclusiveRange(start, end) {
  let node = start;
  while (node) {
    const next = node.nextSibling;
    node.remove();
    if (node === end) break;
    node = next;
  }
}

function moveInclusiveRange(start, end, before) {
  if (end.nextSibling === before) return;
  const fragment = document.createDocumentFragment();
  let node = start;
  while (node) {
    const next = node.nextSibling;
    fragment.appendChild(node);
    if (node === end) break;
    node = next;
  }
  before.parentNode.insertBefore(fragment, before);
}

// Retain a longest increasing subsequence of old positions. A distant swap
// then moves only the two swapped ranges, instead of every row between them.
// Mount and hydration share this ordering contract; -1 marks a new block.
function reorderKeyedBlocks(blocks, positions, end) {
  const tails = [];
  const previous = new Array(positions.length).fill(-1);
  for (let index = 0; index < positions.length; index += 1) {
    if (positions[index] < 0) continue;
    let low = 0;
    let high = tails.length;
    // The usual unchanged/append case is already ordered: keep it linear.
    if (high === 0 || positions[tails[high - 1]] < positions[index]) low = high;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (positions[tails[middle]] < positions[index]) low = middle + 1;
      else high = middle;
    }
    if (low > 0) previous[index] = tails[low - 1];
    tails[low] = index;
  }
  let retained = tails.length > 0 ? tails[tails.length - 1] : -1;
  let before = end;
  for (let index = blocks.length - 1; index >= 0; index -= 1) {
    const block = blocks[index];
    if (index === retained) retained = previous[retained];
    else moveInclusiveRange(block.start, block.end, before);
    before = block.start;
  }
}

export class NoxidAuthorizationError extends Error {
  constructor(code, subject, detail) {
    super(`${code}: ${subject}`);
    this.name = "NoxidAuthorizationError";
    this.code = code;
    this.subject = subject;
    this.detail = detail;
  }
}

export function authorizeComponent(options, subject, capabilities = [], middleware = []) {
  const applied = new Set(options?.middleware?.applied ?? []);
  for (const name of middleware) {
    if (!applied.has(name)) {
      throw new NoxidAuthorizationError("MIDDLEWARE_NOT_APPLIED", subject, { middleware: name });
    }
  }
  if (capabilities.length === 0) return true;
  const authorize = options?.authorizeComponent;
  if (typeof authorize !== "function") {
    throw new NoxidAuthorizationError("COMPONENT_AUTHORIZER_MISSING", subject, { capabilities });
  }
  for (const capability of capabilities) {
    const allowed = authorize(capability, subject, options?.route ?? null);
    if (allowed && typeof allowed.then === "function") {
      throw new NoxidAuthorizationError("ASYNC_COMPONENT_AUTHORIZATION", subject, { capability });
    }
    if (allowed !== true) {
      throw new NoxidAuthorizationError("COMPONENT_CAPABILITY_DENIED", subject, { capability });
    }
  }
  return true;
}

function findRegionEnd(marker, expected) {
  const start = marker.data;
  let nested = 0;
  let node = marker.nextSibling;
  while (node) {
    if (node.nodeType === Node.COMMENT_NODE) {
      if (node.data === start) nested += 1;
      else if (node.data === expected) {
        if (nested === 0) return node;
        nested -= 1;
      }
    }
    node = node.nextSibling;
  }
  throw new Error(`Missing compiler DOM region end marker: ${expected}`);
}

function findHydratedItemEnd(start, itemPrefix, itemEnd, regionEnd) {
  let nested = 0;
  let node = start.nextSibling;
  while (node && node !== regionEnd) {
    if (node.nodeType === Node.COMMENT_NODE) {
      if (node.data.startsWith(itemPrefix)) nested += 1;
      else if (node.data === itemEnd) {
        if (nested === 0) return node;
        nested -= 1;
      }
    }
    node = node.nextSibling;
  }
  return null;
}

function takeBetween(start, end) {
  const fragment = document.createDocumentFragment();
  let node = start.nextSibling;
  while (node && node !== end) {
    const next = node.nextSibling;
    fragment.appendChild(node);
    node = next;
  }
  return fragment;
}

function clearBetween(start, end) {
  let node = start.nextSibling;
  while (node && node !== end) {
    const next = node.nextSibling;
    node.remove();
    node = next;
  }
}

function encodeRegionKey(value) {
  const prefix = typeof value === "number" ? "n:" : "s:";
  return `${prefix}${encodeURIComponent(String(value)).replaceAll("-", "%2D")}`;
}

export function hydrateComponent(marker, endMarker, hydrate, mount, props, handlers, parentOwner, metadata = null, runtimeOptions = {}) {
  const identity = devtoolsIdentity("dom-region", metadata, parentOwner);
  const end = findRegionEnd(marker, endMarker);
  marker.__noxidTemplateEnd = end;
  const target = takeBetween(marker, end);
  const hasServerContent = target.childNodes.length > 0;
  emitDevtools("dom.region.hydrated", { ...identity, payload: { kind: "component", adopted: hasServerContent } });
  const activate = hasServerContent ? hydrate : mount;
  const instance = activate(target, props, handlers, parentOwner, runtimeOptions);
  end.parentNode.insertBefore(target, end);
  onCleanup(instance.owner, () => {
    removeOwnedRange(marker, end);
    emitDevtools("dom.region.disposed", identity);
  });
  return instance;
}

export function hydrateIf(marker, endMarker, condition, parentOwner, activate, metadata = null, transition = null) {
  const source = toSource(condition);
  const identity = devtoolsIdentity("dom-region", metadata, parentOwner);
  const end = findRegionEnd(marker, endMarker);
  let blockOwner = null;
  let presence = null;
  let initial = true;
  emitDevtools("dom.region.hydrated", { ...identity, payload: { kind: "if" } });
  effect(() => {
    if (source.get()) {
      if (blockOwner) {
        presence?.setPresent(true);
        return;
      }
      blockOwner = createOwner(parentOwner, { semanticId: identity.semanticId, label: "if-block" });
      const mountedOwner = blockOwner;
      const adopted = initial && marker.nextSibling !== end;
      const target = adopted ? takeBetween(marker, end) : document.createDocumentFragment();
      activate(target, blockOwner, adopted);
      end.parentNode.insertBefore(target, end);
      onCleanup(blockOwner, () => clearBetween(marker, end));
      if (transition) {
        presence = createPresence(null, blockOwner, {
          present: true,
          exitAnimation: true,
          exitTimeoutMilliseconds: transition.durationMilliseconds,
          onUnmount() {
            if (blockOwner !== mountedOwner) return;
            blockOwner = null;
            presence = null;
            disposeOwner(mountedOwner);
          },
        }, { semanticId: transition.semanticId });
      }
      emitDevtools("dom.region.updated", { ...identity, payload: { kind: "if", active: true, adopted } });
    } else {
      if (blockOwner) {
        if (presence) {
          presence.setPresent(false);
        } else {
          const previous = blockOwner;
          blockOwner = null;
          disposeOwner(previous);
        }
      } else if (initial) {
        clearBetween(marker, end);
      }
      emitDevtools("dom.region.updated", { ...identity, payload: { kind: "if", active: false } });
    }
    initial = false;
  }, parentOwner, [source]);
  onCleanup(parentOwner, () => {
    if (blockOwner) disposeOwner(blockOwner);
    blockOwner = null;
    presence = null;
    clearBetween(marker, end);
    end.remove();
    emitDevtools("dom.region.disposed", identity);
  });
}

export function hydrateMatch(marker, endMarker, value, parentOwner, cases, metadata = null) {
  const source = toSource(value);
  const identity = devtoolsIdentity("dom-region", metadata, parentOwner);
  const end = findRegionEnd(marker, endMarker);
  const ordinal = marker.data.slice("noxid-match-".length);
  let blockOwner = null;
  let initial = true;
  emitDevtools("dom.region.hydrated", { ...identity, payload: { kind: "match" } });
  effect(() => {
    const current = source.get();
    if (!current || typeof current.tag !== "string") throw new Error("Match value is not a tagged union");
    const render = cases[current.tag];
    if (!render) throw new Error(`Unhandled match variant: ${current.tag}`);
    if (blockOwner) disposeOwner(blockOwner);
    const caseMarker = marker.nextSibling;
    const expected = `noxid-match-case-${ordinal}:${encodeRegionKey(current.tag)}`;
    const adopted = initial && caseMarker?.nodeType === Node.COMMENT_NODE && caseMarker.data === expected;
    if (!adopted) clearBetween(marker, end);
    blockOwner = createOwner(parentOwner, { semanticId: identity.semanticId, label: `#match ${current.tag}` });
    const target = adopted ? takeBetween(marker, end) : document.createDocumentFragment();
    render(target, blockOwner, current, adopted);
    end.parentNode.insertBefore(target, end);
    onCleanup(blockOwner, () => clearBetween(marker, end));
    emitDevtools("dom.region.updated", { ...identity, payload: { kind: "match", variant: current.tag, adopted } });
    initial = false;
  }, parentOwner, [source]);
  onCleanup(parentOwner, () => {
    if (blockOwner) disposeOwner(blockOwner);
    blockOwner = null;
    clearBetween(marker, end);
    end.remove();
    emitDevtools("dom.region.disposed", identity);
  });
}

export function hydrateFor(marker, endMarker, collection, parentOwner, keyOf, render, metadata = null) {
  const source = toSource(collection);
  const identity = devtoolsIdentity("dom-region", metadata, parentOwner);
  const end = findRegionEnd(marker, endMarker);
  const regionKind = typeof metadata === "object" && metadata?.regionKind ? metadata.regionKind : "for";
  const ordinal = marker.data.slice(`noxid-${regionKind}-`.length);
  const itemPrefix = `noxid-${regionKind}-item-${ordinal}:`;
  const itemEnd = `noxid-${regionKind}-item-end-${ordinal}`;
  const serverBlocks = new Map();
  let cursor = marker.nextSibling;
  while (cursor && cursor !== end) {
    if (cursor.nodeType !== Node.COMMENT_NODE || !cursor.data.startsWith(itemPrefix)) {
      throw new Error(`Invalid hydrated #for region: ${identity.semanticId ?? ordinal}`);
    }
    const start = cursor;
    cursor = findHydratedItemEnd(start, itemPrefix, itemEnd, end);
    if (!cursor) throw new Error(`Missing hydrated #for item end: ${identity.semanticId ?? ordinal}`);
    const key = start.data.slice(itemPrefix.length);
    if (serverBlocks.has(key)) throw new Error(`Duplicate hydrated #for key: ${key}`);
    serverBlocks.set(key, { start, end: cursor });
    cursor = cursor.nextSibling;
  }
  let blocks = [];
  emitDevtools("dom.region.hydrated", { ...identity, payload: { kind: "for", serverItemCount: serverBlocks.size } });

  function duplicateKey(key) {
    const error = new Error(`Duplicate #for key: ${String(key)}`);
    error.code = "DUPLICATE_FOR_KEY";
    error.key = key;
    throw error;
  }

  function createBlock(value, key, before, existing = null) {
    const owner = createOwner(parentOwner, { semanticId: identity.semanticId, label: `#for item` });
    const item = signal(value);
    const start = existing?.start ?? document.createComment(`${itemPrefix}${encodeRegionKey(key)}`);
    const blockEnd = existing?.end ?? document.createComment(itemEnd);
    const target = existing ? takeBetween(start, blockEnd) : document.createDocumentFragment();
    render(target, owner, item, Boolean(existing));
    if (existing) blockEnd.parentNode.insertBefore(target, blockEnd);
    else {
      const range = document.createDocumentFragment();
      range.appendChild(start);
      range.appendChild(target);
      range.appendChild(blockEnd);
      before.parentNode.insertBefore(range, before);
    }
    const block = { key, owner, item, start, end: blockEnd };
    onCleanup(owner, () => removeInclusiveRange(start, blockEnd));
    return block;
  }

  function reconcile(values, adopting = false) {
    if (!Array.isArray(values)) throw new TypeError("Keyed loop source must be an array");
    // Validate all keys before updating signals or creating owners. A rejected
    // reset must leave the currently mounted list intact.
    const keys = values.map((value) => keyOf(toSource(value)));
    const seen = new Set();
    for (const key of keys) {
      if (seen.has(key)) duplicateKey(key);
      seen.add(key);
    }
    const existing = new Map(blocks.map((block, index) => [block.key, { block, index }]));
    const next = [];
    const positions = [];
    for (let index = 0; index < values.length; index += 1) {
      const value = values[index];
      const key = keys[index];
      const entry = existing.get(key);
      positions.push(entry?.index ?? -1);
      if (entry) {
        existing.delete(key);
        entry.block.item.set(value);
        next.push(entry.block);
      } else {
        const hydrated = adopting ? serverBlocks.get(encodeRegionKey(key)) ?? null : null;
        if (hydrated) serverBlocks.delete(encodeRegionKey(key));
        next.push(createBlock(value, key, end, hydrated));
      }
    }
    for (const { block } of existing.values()) disposeOwner(block.owner);
    for (const block of serverBlocks.values()) removeInclusiveRange(block.start, block.end);
    serverBlocks.clear();
    reorderKeyedBlocks(next, positions, end);
    blocks = next;
    emitDevtools("dom.region.updated", { ...identity, payload: { kind: "for", itemCount: blocks.length, adopted: adopting } });
  }

  function apply(operation) {
    if (operation.kind === "reset") return reconcile(operation.values);
    if (operation.kind === "insert") {
      const item = toSource(operation.value);
      const key = keyOf(item);
      if (blocks.some((block) => Object.is(block.key, key))) duplicateKey(key);
      const before = blocks[operation.index]?.start ?? end;
      blocks.splice(operation.index, 0, createBlock(operation.value, key, before));
      return;
    }
    if (operation.kind === "delete") {
      const [block] = blocks.splice(operation.index, 1);
      if (block) disposeOwner(block.owner);
      return;
    }
    if (operation.kind === "move") {
      const [block] = blocks.splice(operation.from, 1);
      if (!block) return;
      blocks.splice(operation.to, 0, block);
      moveInclusiveRange(block.start, block.end, blocks[operation.to + 1]?.start ?? end);
    }
  }

  reconcile(source.get(), true);
  if (typeof source.subscribeOperations === "function") {
    const observer = {
      dependencies: new Set(), disposed: false, operations: [],
      schedule(operation) { observer.operations.push(operation); enqueue(observer); },
      run() { if (!observer.disposed) for (const operation of observer.operations.splice(0)) apply(operation); },
      dispose() { observer.disposed = true; pending.delete(observer); observer.operations.length = 0; },
    };
    const unsubscribe = source.subscribeOperations(observer);
    onCleanup(parentOwner, () => { unsubscribe(); observer.dispose(); });
  } else {
    effect(() => reconcile(source.get()), parentOwner, [source]);
  }
  onCleanup(parentOwner, () => {
    for (const block of blocks.splice(0)) disposeOwner(block.owner);
    clearBetween(marker, end);
    end.remove();
    emitDevtools("dom.region.disposed", identity);
  });
}

export function hydrateStream(marker, endMarker, events, parentOwner, cases, metadata = null) {
  return hydrateFor(
    marker,
    endMarker,
    events,
    parentOwner,
    (envelope) => envelope.get().sequence,
    (target, blockOwner, envelope, hydratingBlock) => {
      const event = envelope.get().event;
      if (!event || typeof event.tag !== "string") throw new TypeError("Stream event is not tagged");
      const render = cases[event.tag];
      if (!render) throw new Error(`Unhandled stream event: ${event.tag}`);
      render(target, blockOwner, event, hydratingBlock);
    },
    {
      semanticId: typeof metadata === "string" ? metadata : metadata?.semanticId ?? null,
      regionKind: "stream",
    },
  );
}

export function mountComponent(marker, mount, props, handlers, parentOwner, metadata = null) {
  const identity = devtoolsIdentity("dom-region", metadata, parentOwner);
  emitDevtools("dom.region.created", { ...identity, payload: { kind: "component" } });
  const end = document.createComment("noxid-component-end");
  marker.__noxidTemplateEnd = end;
  marker.parentNode.insertBefore(end, marker.nextSibling);
  const target = document.createDocumentFragment();
  const instance = mount(target, props, handlers, parentOwner);
  end.parentNode.insertBefore(target, end);
  onCleanup(instance.owner, () => {
    removeOwnedRange(marker, end);
    emitDevtools("dom.region.disposed", identity);
  });
  return instance;
}

export function mountIf(marker, condition, parentOwner, render, metadata = null, transition = null) {
  const source = toSource(condition);
  const identity = devtoolsIdentity("dom-region", metadata, parentOwner);
  let blockOwner = null;
  let presence = null;
  emitDevtools("dom.region.created", { ...identity, payload: { kind: "if" } });
  effect(() => {
    if (source.get()) {
      if (blockOwner) {
        presence?.setPresent(true);
        return;
      }
      blockOwner = createOwner(parentOwner, { semanticId: identity.semanticId, label: "if-block" });
      const mountedOwner = blockOwner;
      const end = document.createComment("noxid-if-end");
      marker.parentNode.insertBefore(end, marker.nextSibling);
      const target = document.createDocumentFragment();
      render(target, blockOwner);
      end.parentNode.insertBefore(target, end);
      onCleanup(blockOwner, () => removeOwnedRange(marker, end));
      if (transition) {
        presence = createPresence(null, blockOwner, {
          present: true,
          exitAnimation: true,
          exitTimeoutMilliseconds: transition.durationMilliseconds,
          onUnmount() {
            if (blockOwner !== mountedOwner) return;
            blockOwner = null;
            presence = null;
            disposeOwner(mountedOwner);
          },
        }, { semanticId: transition.semanticId });
      }
      emitDevtools("dom.region.updated", { ...identity, payload: { kind: "if", active: true } });
    } else if (blockOwner) {
      if (presence) {
        presence.setPresent(false);
      } else {
        const previous = blockOwner;
        blockOwner = null;
        disposeOwner(previous);
      }
      emitDevtools("dom.region.updated", { ...identity, payload: { kind: "if", active: false } });
    }
  }, parentOwner, [source]);
  onCleanup(parentOwner, () => {
    if (blockOwner) disposeOwner(blockOwner);
    blockOwner = null;
    presence = null;
    emitDevtools("dom.region.disposed", identity);
  });
}

export function mountMatch(marker, value, parentOwner, cases, metadata = null) {
  const source = toSource(value);
  const identity = devtoolsIdentity("dom-region", metadata, parentOwner);
  let blockOwner = null;
  emitDevtools("dom.region.created", { ...identity, payload: { kind: "match" } });
  effect(() => {
    const current = source.get();
    if (!current || typeof current.tag !== "string") {
      throw new Error("Match value is not a tagged union");
    }
    const render = cases[current.tag];
    if (!render) throw new Error(`Unhandled match variant: ${current.tag}`);
    if (blockOwner) disposeOwner(blockOwner);
    blockOwner = createOwner(parentOwner, { semanticId: identity.semanticId, label: `#match ${current.tag}` });
    const end = document.createComment("noxid-match-end");
    marker.parentNode.insertBefore(end, marker.nextSibling);
    const target = document.createDocumentFragment();
    render(target, blockOwner, current);
    end.parentNode.insertBefore(target, end);
    onCleanup(blockOwner, () => removeOwnedRange(marker, end));
    emitDevtools("dom.region.updated", { ...identity, payload: { kind: "match", variant: current.tag } });
  }, parentOwner, [source]);
  onCleanup(parentOwner, () => {
    if (blockOwner) disposeOwner(blockOwner);
    blockOwner = null;
    emitDevtools("dom.region.disposed", identity);
  });
}



export function mountFor(marker, collection, parentOwner, keyOf, render, metadata = null) {
  const source = toSource(collection);
  const regionKind = typeof metadata === "object" ? metadata?.regionKind ?? "for" : "for";
  const identity = devtoolsIdentity("dom-region", metadata, parentOwner);
  const end = document.createComment("noxid-for-end");
  marker.parentNode.insertBefore(end, marker.nextSibling);
  let blocks = [];
  emitDevtools("dom.region.created", { ...identity, payload: { kind: regionKind } });

  function duplicateKey(key) {
    const error = new Error(`Duplicate #for key: ${String(key)}`);
    error.code = "DUPLICATE_FOR_KEY";
    error.key = key;
    throw error;
  }

  function createBlock(value, key, before) {
    const owner = createOwner(parentOwner, { semanticId: identity.semanticId, label: `#${regionKind} item` });
    const item = signal(value, { owner });
    const start = document.createComment(`noxid-for-item:${String(key)}`);
    const blockEnd = document.createComment("noxid-for-item-end");
    const content = document.createDocumentFragment();
    render(content, owner, item);
    const range = document.createDocumentFragment();
    range.appendChild(start);
    range.appendChild(content);
    range.appendChild(blockEnd);
    before.parentNode.insertBefore(range, before);
    const block = { key, owner, item, start, end: blockEnd };
    onCleanup(owner, () => removeInclusiveRange(start, blockEnd));
    return block;
  }

  function reconcile(values) {
    if (!Array.isArray(values)) throw new TypeError("Keyed loop source must be an array");
    // Validate all keys before updating signals or creating owners. A rejected
    // reset must leave the currently mounted list intact.
    const keys = values.map((value) => keyOf(toSource(value)));
    const seen = new Set();
    for (const key of keys) {
      if (seen.has(key)) duplicateKey(key);
      seen.add(key);
    }
    const existing = new Map(blocks.map((block, index) => [block.key, { block, index }]));
    const next = [];
    const positions = [];
    for (let index = 0; index < values.length; index += 1) {
      const value = values[index];
      const key = keys[index];
      const entry = existing.get(key);
      positions.push(entry?.index ?? -1);
      if (entry) {
        existing.delete(key);
        entry.block.item.set(value);
        next.push(entry.block);
      } else {
        next.push(createBlock(value, key, end));
      }
    }
    for (const { block } of existing.values()) disposeOwner(block.owner);
    reorderKeyedBlocks(next, positions, end);
    blocks = next;
    emitDevtools("dom.region.updated", { ...identity, payload: { kind: regionKind, itemCount: blocks.length } });
  }

  function apply(operation) {
    if (operation.kind === "reset") {
      reconcile(operation.values);
      return;
    }
    if (operation.kind === "insert") {
      const item = toSource(operation.value);
      const key = keyOf(item);
      if (blocks.some((block) => Object.is(block.key, key))) duplicateKey(key);
      const before = blocks[operation.index]?.start ?? end;
      const block = createBlock(operation.value, key, before);
      blocks.splice(operation.index, 0, block);
      return;
    }
    if (operation.kind === "delete") {
      const [block] = blocks.splice(operation.index, 1);
      if (block) disposeOwner(block.owner);
      return;
    }
    if (operation.kind === "move") {
      const [block] = blocks.splice(operation.from, 1);
      if (!block) return;
      blocks.splice(operation.to, 0, block);
      const before = blocks[operation.to + 1]?.start ?? end;
      moveInclusiveRange(block.start, block.end, before);
    }
  }

  reconcile(source.get());
  if (typeof source.subscribeOperations === "function") {
    const observer = {
      dependencies: new Set(),
      disposed: false,
      operations: [],
      schedule(operation) { observer.operations.push(operation); enqueue(observer); },
      run() {
        if (observer.disposed) return;
        for (const operation of observer.operations.splice(0)) apply(operation);
      },
      dispose() { observer.disposed = true; pending.delete(observer); observer.operations.length = 0; }
    };
    const unsubscribe = source.subscribeOperations(observer);
    onCleanup(parentOwner, () => { unsubscribe(); observer.dispose(); });
  } else {
    effect(() => reconcile(source.get()), parentOwner, [source]);
  }
  onCleanup(parentOwner, () => {
    for (const block of blocks.splice(0)) disposeOwner(block.owner);
    end.remove();
    emitDevtools("dom.region.disposed", identity);
  });
}

export function mountStream(marker, events, parentOwner, cases, metadata = null) {
  return mountFor(
    marker,
    events,
    parentOwner,
    (envelope) => envelope.get().sequence,
    (target, blockOwner, envelope) => {
      const event = envelope.get().event;
      if (!event || typeof event.tag !== "string") throw new TypeError("Stream event is not tagged");
      const render = cases[event.tag];
      if (!render) throw new Error(`Unhandled stream event: ${event.tag}`);
      render(target, blockOwner, event);
    },
    {
      semanticId: typeof metadata === "string" ? metadata : metadata?.semanticId ?? null,
      regionKind: "stream",
    },
  );
}

// Shared by resources and typed streams. This helper belongs to the runtime
// kernel because either feature may be independently retained by pruning.
function abortError(reason = "Resource request was cancelled") {
  const error = new Error(typeof reason === "string" ? reason : "Resource request was cancelled");
  error.name = "AbortError";
  error.code = "RESOURCE_ABORTED";
  return error;
}

export class ResourceExecutionError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = "ResourceExecutionError";
    this.code = code;
    this.resource = details.resource ?? null;
    this.status = details.status ?? null;
    this.attempt = details.attempt ?? null;
    this.capability = details.capability ?? null;
    this.parameter = details.parameter ?? null;
    this.expected = details.expected ?? null;
    this.retriable = details.retriable === true;
    if (details.cause !== undefined) this.cause = details.cause;
  }

  toJSON() {
    return {
      code: this.code,
      resource: this.resource,
      status: this.status,
      attempt: this.attempt,
      capability: this.capability,
      parameter: this.parameter,
      expected: this.expected,
      retriable: this.retriable,
    };
  }
}

function resourceFailure(code, message, config, details = {}) {
  return new ResourceExecutionError(code, message, { resource: config.id, ...details });
}

function resourceState(tag, fields = {}) {
  if ((tag === "Ready" || tag === "Refreshing") && Object.prototype.hasOwnProperty.call(fields, "data")) {
    return Object.freeze({ tag, ...fields, value: fields.data });
  }
  if (tag === "Failed" && Object.prototype.hasOwnProperty.call(fields, "error")) {
    return Object.freeze({ tag, ...fields, value: fields.error });
  }
  return Object.freeze({ tag, ...fields });
}

function resourceParameterMatches(value, type) {
  if (type.startsWith("Optional<") && type.endsWith(">")) {
    return value == null || resourceParameterMatches(value, type.slice(9, -1));
  }
  if (type === "String" || type === "Date") return typeof value === "string";
  if (type === "Boolean") return typeof value === "boolean";
  if (type === "Int") return typeof value === "number" && Number.isSafeInteger(value);
  if (type === "Number" || type === "Float") return typeof value === "number" && Number.isFinite(value);
  if (type === "AgentError") return value !== null && typeof value === "object" && typeof value.code === "string" && typeof value.message === "string";
  return false;
}

function normalizeResourceParameters(config, input) {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    throw resourceFailure("RESOURCE_PARAMETERS_INVALID", `${config.id} parameters must be an object`, config);
  }
  const normalized = Object.create(null);
  for (const parameter of config.parameters) {
    const value = input[parameter.name];
    if (!resourceParameterMatches(value, parameter.type)) {
      throw resourceFailure(
        "RESOURCE_PARAMETER_INVALID",
        `${config.id}.${parameter.name} expected ${parameter.type}`,
        config,
        { parameter: parameter.name, expected: parameter.type },
      );
    }
    normalized[parameter.name] = value;
  }
  return Object.freeze(normalized);
}

function buildResourceUrl(config, parameters) {
  return config.request.urlTemplate.replace(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (_, name) => {
    if (!Object.prototype.hasOwnProperty.call(parameters, name)) {
      throw resourceFailure("RESOURCE_PARAMETER_MISSING", `${config.id} is missing URL parameter ${name}`, config, { parameter: name });
    }
    return encodeURIComponent(String(parameters[name]));
  });
}

function isAbort(error, signal) {
  return signal.aborted || error?.name === "AbortError" || error?.code === "RESOURCE_ABORTED";
}

function stableQueryValue(value, seen = new Set()) {
  if (value === null) return "null";
  if (typeof value === "string" || typeof value === "boolean") return JSON.stringify(value);
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new TypeError("Query keys may only contain finite numbers");
    return Object.is(value, -0) ? "0" : String(value);
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) throw new TypeError("Query keys cannot be circular");
    seen.add(value);
    const output = `[${value.map((item) => stableQueryValue(item, seen)).join(",")}]`;
    seen.delete(value);
    return output;
  }
  if (typeof value === "object") {
    if (seen.has(value)) throw new TypeError("Query keys cannot be circular");
    seen.add(value);
    const output = `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableQueryValue(value[key], seen)}`).join(",")}}`;
    seen.delete(value);
    return output;
  }
  throw new TypeError(`Query keys cannot contain ${typeof value}`);
}

function normalizeQueryKey(queryKey) {
  if (!Array.isArray(queryKey) || queryKey.length === 0) {
    throw new TypeError("A query key must be a non-empty array");
  }
  stableQueryValue(queryKey);
  return Object.freeze([...queryKey]);
}

function queryKeyStartsWith(queryKey, prefix) {
  if (prefix.length > queryKey.length) return false;
  for (let index = 0; index < prefix.length; index += 1) {
    if (stableQueryValue(queryKey[index]) !== stableQueryValue(prefix[index])) return false;
  }
  return true;
}

function queryOptionsArgument(options, method) {
  if (!options || typeof options !== "object" || Array.isArray(options)) {
    throw new TypeError(`${method} expects an options object`);
  }
  normalizeQueryKey(options.queryKey);
  return options;
}

const queryClientResourceBridge = Symbol("noxid.query-client.resource-bridge");

/**
 * Noxid's owner-aware, dependency-free QueryClient. It intentionally mirrors the
 * imperative TanStack QueryClient surface while sharing cache entries with
 * compiler-generated resources.
 */
export function createQueryClient(configuration = {}) {
  const runtimeId = nextRuntimeId("query-client");
  const id = typeof configuration.id === "string" ? configuration.id : runtimeId;
  const now = typeof configuration.now === "function" ? configuration.now : () => Date.now();
  const entries = new Map();
  const queryDefaults = [];
  const mutationDefaults = [];
  const querySubscribers = new Set();
  const mutationSubscribers = new Set();
  const mutations = new Set();
  const resourceValidators = new Map();
  const globalDefaults = {
    queries: Object.freeze({ ...(configuration.defaultOptions?.queries ?? {}) }),
    mutations: Object.freeze({ ...(configuration.defaultOptions?.mutations ?? {}) }),
  };

  function hash(queryKey) {
    return stableQueryValue(normalizeQueryKey(queryKey));
  }

  function matchingDefaults(definitions, key) {
    return definitions.reduce(
      (combined, definition) => queryKeyStartsWith(key, definition.queryKey)
        ? { ...combined, ...definition.options }
        : combined,
      {},
    );
  }

  function defaultedQueryOptions(options) {
    const key = normalizeQueryKey(options.queryKey);
    return {
      ...globalDefaults.queries,
      ...matchingDefaults(queryDefaults, key),
      ...options,
      queryKey: key,
    };
  }

  function stateOf(entry) {
    return Object.freeze({
      data: entry.hasData ? entry.data : undefined,
      dataUpdatedAt: entry.dataUpdatedAt,
      error: entry.error,
      errorUpdatedAt: entry.errorUpdatedAt,
      status: entry.status,
      fetchStatus: entry.fetchTokens.size > 0 || entry.promise ? "fetching" : "idle",
      isInvalidated: entry.invalidated,
    });
  }

  function queryView(entry) {
    return Object.freeze({
      queryKey: entry.queryKey,
      queryHash: entry.queryHash,
      state: stateOf(entry),
    });
  }

  function emitQuery(entry, kind, payload = {}) {
    const event = Object.freeze({ type: kind, query: queryView(entry) });
    for (const subscriber of [...querySubscribers]) subscriber(event);
    for (const observer of [...entry.observers]) observer.changed?.(stateOf(entry), kind);
    emitDevtools(`query.${kind}`, {
      semanticId: entry.queryKey[0] ?? id,
      runtimeId,
      payload: { queryHash: entry.queryHash, ...payload },
    });
  }

  function scheduleGc(entry) {
    if (entry.gcTimer) clearTimeout(entry.gcTimer);
    entry.gcTimer = null;
    if (entry.observers.size > 0 || entry.promise || entry.fetchTokens.size > 0) return;
    const gcTime = Number.isFinite(entry.gcTime) ? Math.max(0, entry.gcTime) : 300000;
    if (gcTime === Infinity) return;
    if (gcTime === 0) {
      entries.delete(entry.queryHash);
      return;
    }
    entry.gcTimer = setTimeout(() => {
      if (entry.observers.size === 0 && !entry.promise && entry.fetchTokens.size === 0) {
        entries.delete(entry.queryHash);
        emitQuery(entry, "removed", { reason: "garbage-collected" });
      }
    }, gcTime);
    entry.gcTimer.unref?.();
  }

  function ensureEntry(queryKey, options = {}) {
    const key = normalizeQueryKey(queryKey);
    const queryHash = stableQueryValue(key);
    let entry = entries.get(queryHash);
    if (!entry) {
      const hasInitial = Object.prototype.hasOwnProperty.call(options, "initialData") && options.initialData !== undefined;
      const initialData = hasInitial && resourceValidators.has(key[0])
        ? resourceValidators.get(key[0])(options.initialData)
        : options.initialData;
      entry = {
        queryKey: key,
        queryHash,
        hasData: hasInitial,
        data: initialData,
        initialData,
        dataUpdatedAt: hasInitial ? now() : 0,
        error: null,
        errorUpdatedAt: 0,
        status: hasInitial ? "success" : "pending",
        invalidated: false,
        queryFn: null,
        options: {},
        promise: null,
        controller: null,
        observers: new Set(),
        fetchTokens: new Set(),
        gcTime: options.gcTime,
        gcTimer: null,
      };
      entries.set(queryHash, entry);
      emitQuery(entry, "added");
    }
    if (entry.gcTimer) clearTimeout(entry.gcTimer);
    entry.gcTimer = null;
    if (Number.isFinite(options.gcTime) || options.gcTime === Infinity) entry.gcTime = options.gcTime;
    return entry;
  }

  function matches(entry, filters = {}) {
    if (filters.queryKey) {
      const filterKey = normalizeQueryKey(filters.queryKey);
      if (filters.exact ? entry.queryHash !== stableQueryValue(filterKey) : !queryKeyStartsWith(entry.queryKey, filterKey)) return false;
    }
    const active = entry.observers.size > 0;
    if (filters.type === "active" && !active) return false;
    if (filters.type === "inactive" && active) return false;
    if (typeof filters.stale === "boolean") {
      const staleTime = Number.isFinite(entry.options.staleTime) ? Math.max(0, entry.options.staleTime) : 0;
      const stale = entry.invalidated || !entry.hasData || now() - entry.dataUpdatedAt >= staleTime;
      if (filters.stale !== stale) return false;
    }
    return typeof filters.predicate !== "function" || filters.predicate(queryView(entry)) === true;
  }

  function findAll(filters = {}) {
    return [...entries.values()].filter((entry) => matches(entry, filters));
  }

  function write(entry, updater, updatedAt = now(), eventKind = "updated", validateData = true) {
    const previous = entry.hasData ? entry.data : undefined;
    const candidate = typeof updater === "function" ? updater(previous) : updater;
    if (candidate === undefined) return previous;
    const validate = validateData ? resourceValidators.get(entry.queryKey[0]) : null;
    const next = validate ? validate(candidate) : candidate;
    entry.hasData = true;
    entry.data = next;
    entry.dataUpdatedAt = updatedAt;
    entry.error = null;
    entry.status = "success";
    entry.invalidated = false;
    emitQuery(entry, eventKind);
    return next;
  }

  function retryCount(retry) {
    if (retry === true) return 3;
    if (retry === false) return 0;
    return Number.isInteger(retry) && retry >= 0 ? retry : 0;
  }

  async function execute(entry, rawOptions) {
    if (entry.promise) return entry.promise;
    const options = defaultedQueryOptions(rawOptions);
    if (typeof options.queryFn !== "function") throw new TypeError("query requires a queryFn");
    entry.queryFn = options.queryFn;
    entry.options = options;
    entry.gcTime = Number.isFinite(options.gcTime) || options.gcTime === Infinity ? options.gcTime : entry.gcTime;
    const controller = new AbortController();
    entry.controller = controller;
    entry.status = entry.hasData ? entry.status : "pending";
    emitQuery(entry, "fetching");
    const maxAttempts = retryCount(options.retry) + 1;
    entry.promise = (async () => {
      let failure;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          const value = await options.queryFn(Object.freeze({
            queryKey: entry.queryKey,
            signal: controller.signal,
            meta: options.meta,
          }));
          if (value === undefined) throw new TypeError("A query function must not resolve to undefined");
          return write(entry, value, now(), "succeeded");
        } catch (error) {
          if (controller.signal.aborted || error?.name === "AbortError") throw abortError(controller.signal.reason);
          failure = error;
          const shouldRetry = typeof options.retry === "function"
            ? options.retry(attempt - 1, error) === true
            : attempt < maxAttempts;
          if (!shouldRetry) break;
          const delay = typeof options.retryDelay === "function"
            ? options.retryDelay(attempt - 1, error)
            : Number.isFinite(options.retryDelay) ? options.retryDelay : Math.min(1000 * (2 ** (attempt - 1)), 30000);
          await retryDelay(delay, controller.signal);
        }
      }
      entry.error = failure;
      entry.errorUpdatedAt = now();
      entry.status = "error";
      emitQuery(entry, "failed");
      throw failure;
    })().finally(() => {
      entry.promise = null;
      entry.controller = null;
      emitQuery(entry, "settled");
      scheduleGc(entry);
    });
    return entry.promise;
  }

  async function query(rawOptions) {
    const options = defaultedQueryOptions(queryOptionsArgument(rawOptions, "query"));
    const entry = ensureEntry(options.queryKey, options);
    const staleTime = Number.isFinite(options.staleTime) ? Math.max(0, options.staleTime) : 0;
    if (entry.hasData && !entry.invalidated && now() - entry.dataUpdatedAt < staleTime) return entry.data;
    return execute(entry, options);
  }

  async function infiniteQuery(rawOptions) {
    const options = queryOptionsArgument(rawOptions, "infiniteQuery");
    if (typeof options.queryFn !== "function") throw new TypeError("infiniteQuery requires a queryFn");
    const pageCount = Number.isInteger(options.pages) && options.pages > 0 ? options.pages : 1;
    return query({
      ...options,
      async queryFn(context) {
        const pages = [];
        const pageParams = [];
        let pageParam = options.initialPageParam;
        for (let index = 0; index < pageCount; index += 1) {
          pageParams.push(pageParam);
          const page = await options.queryFn(Object.freeze({ ...context, pageParam, direction: "forward" }));
          pages.push(page);
          if (index + 1 >= pageCount || typeof options.getNextPageParam !== "function") break;
          pageParam = options.getNextPageParam(page, Object.freeze([...pages]), pageParam, Object.freeze([...pageParams]));
          if (pageParam == null) break;
        }
        return Object.freeze({ pages: Object.freeze(pages), pageParams: Object.freeze(pageParams) });
      },
    });
  }

  async function refetchEntry(entry, options = {}) {
    const observer = [...entry.observers].find((candidate) => typeof candidate.refetch === "function");
    if (observer) return observer.refetch(options);
    if (entry.queryFn) return execute(entry, { ...entry.options, queryKey: entry.queryKey, queryFn: entry.queryFn });
    return entry.hasData ? entry.data : undefined;
  }

  const queryCache = Object.freeze({
    find(filters = {}) { return findAll(filters)[0] ? queryView(findAll(filters)[0]) : undefined; },
    findAll(filters = {}) { return Object.freeze(findAll(filters).map(queryView)); },
    subscribe(listener) {
      if (typeof listener !== "function") throw new TypeError("Query cache subscribers must be functions");
      querySubscribers.add(listener);
      return () => querySubscribers.delete(listener);
    },
    clear() { client.clear(); },
  });

  function mutationView(mutation) {
    return Object.freeze({
      mutationKey: mutation.mutationKey,
      state: Object.freeze({ status: mutation.status, data: mutation.data, error: mutation.error, variables: mutation.variables }),
    });
  }

  function emitMutation(mutation, type) {
    const event = Object.freeze({ type, mutation: mutationView(mutation) });
    for (const subscriber of [...mutationSubscribers]) subscriber(event);
    emitDevtools(`mutation.${type}`, { semanticId: mutation.mutationKey?.[0] ?? id, runtimeId });
  }

  const mutationCache = Object.freeze({
    find(filters = {}) { return [...mutations].find((mutation) => !filters.mutationKey || queryKeyStartsWith(mutation.mutationKey, filters.mutationKey)) ? mutationView([...mutations].find((mutation) => !filters.mutationKey || queryKeyStartsWith(mutation.mutationKey, filters.mutationKey))) : undefined; },
    findAll(filters = {}) { return Object.freeze([...mutations].filter((mutation) => !filters.mutationKey || queryKeyStartsWith(mutation.mutationKey, filters.mutationKey)).map(mutationView)); },
    subscribe(listener) {
      if (typeof listener !== "function") throw new TypeError("Mutation cache subscribers must be functions");
      mutationSubscribers.add(listener);
      return () => mutationSubscribers.delete(listener);
    },
    clear() { mutations.clear(); },
  });

  const client = {
    id,
    query,
    fetchQuery: query,
    async prefetchQuery(options) { await query(options); },
    async ensureQueryData(options) {
      const existing = client.getQueryData(options.queryKey);
      return existing === undefined ? query(options) : existing;
    },
    infiniteQuery,
    fetchInfiniteQuery: infiniteQuery,
    async prefetchInfiniteQuery(options) { await infiniteQuery(options); },
    getQueryData(queryKey) {
      const entry = entries.get(hash(queryKey));
      return entry?.hasData ? entry.data : undefined;
    },
    getQueriesData(filters = {}) {
      return Object.freeze(findAll(filters).filter((entry) => entry.hasData).map((entry) => Object.freeze([entry.queryKey, entry.data])));
    },
    setQueryData(queryKey, updater) {
      const normalized = normalizeQueryKey(queryKey);
      const queryHash = stableQueryValue(normalized);
      const existing = entries.get(queryHash);
      if (!existing) {
        const next = typeof updater === "function" ? updater(undefined) : updater;
        if (next === undefined) return undefined;
        return write(ensureEntry(normalized), next);
      }
      const entry = existing ?? ensureEntry(normalized);
      return write(entry, updater);
    },
    getQueryState(queryKey) {
      const entry = entries.get(hash(queryKey));
      return entry ? stateOf(entry) : undefined;
    },
    setQueriesData(filters, updater) {
      return Object.freeze(findAll(filters).map((entry) => Object.freeze([entry.queryKey, write(entry, updater)])));
    },
    async invalidateQueries(filters = {}, options = {}) {
      const selected = findAll(filters);
      for (const entry of selected) {
        entry.invalidated = true;
        emitQuery(entry, "invalidated");
        for (const observer of [...entry.observers]) observer.invalidated?.(entry.hasData ? entry.data : undefined);
      }
      const refetchType = filters.refetchType ?? "active";
      if (refetchType === "none") return;
      const refetchable = selected.filter((entry) => refetchType === "all" || (refetchType === "active") === (entry.observers.size > 0));
      await Promise.all(refetchable.map((entry) => refetchEntry(entry, options)));
    },
    async refetchQueries(filters = {}, options = {}) {
      const results = await Promise.allSettled(findAll(filters).map((entry) => refetchEntry(entry, options)));
      const failure = results.find((result) => result.status === "rejected");
      if (failure && options.throwOnError === true) throw failure.reason;
    },
    async cancelQueries(filters = {}) {
      for (const entry of findAll(filters)) {
        entry.controller?.abort("Query cancelled");
        for (const observer of [...entry.observers]) observer.cancel?.("Query cancelled");
        emitQuery(entry, "cancelled");
      }
    },
    removeQueries(filters = {}) {
      for (const entry of findAll(filters)) {
        entry.controller?.abort("Query removed");
        if (entry.gcTimer) clearTimeout(entry.gcTimer);
        entries.delete(entry.queryHash);
        for (const observer of [...entry.observers]) observer.reset?.();
        emitQuery(entry, "removed");
      }
    },
    async resetQueries(filters = {}, options = {}) {
      const selected = findAll(filters);
      for (const entry of selected) {
        entry.hasData = entry.initialData !== undefined;
        entry.data = entry.initialData;
        entry.dataUpdatedAt = entry.hasData ? now() : 0;
        entry.error = null;
        entry.errorUpdatedAt = 0;
        entry.invalidated = false;
        entry.status = entry.hasData ? "success" : "pending";
        for (const observer of [...entry.observers]) observer.reset?.(entry.hasData ? entry.data : undefined);
        emitQuery(entry, "reset");
      }
      await Promise.all(selected.filter((entry) => entry.observers.size > 0).map((entry) => refetchEntry(entry, options)));
    },
    isFetching(filters = {}) {
      return findAll(filters).filter((entry) => entry.promise || entry.fetchTokens.size > 0).length;
    },
    isMutating(filters = {}) {
      return [...mutations].filter((mutation) => mutation.status === "pending" && (!filters.mutationKey || queryKeyStartsWith(mutation.mutationKey, filters.mutationKey))).length;
    },
    async mutate(rawOptions) {
      if (!rawOptions || typeof rawOptions.mutationFn !== "function") throw new TypeError("mutate requires a mutationFn");
      const mutationKey = rawOptions.mutationKey ? normalizeQueryKey(rawOptions.mutationKey) : Object.freeze([`mutation:${runtimeId}`]);
      const options = { ...globalDefaults.mutations, ...matchingDefaults(mutationDefaults, mutationKey), ...rawOptions };
      const mutation = { mutationKey, status: "pending", data: undefined, error: null, variables: options.variables };
      mutations.add(mutation);
      emitMutation(mutation, "added");
      try {
        await options.onMutate?.(options.variables);
        mutation.data = await options.mutationFn(options.variables);
        mutation.status = "success";
        await options.onSuccess?.(mutation.data, options.variables);
        emitMutation(mutation, "succeeded");
        return mutation.data;
      } catch (error) {
        mutation.error = error;
        mutation.status = "error";
        await options.onError?.(error, options.variables);
        emitMutation(mutation, "failed");
        throw error;
      } finally {
        await options.onSettled?.(mutation.data, mutation.error, options.variables);
      }
    },
    setDefaultOptions(options = {}) {
      globalDefaults.queries = Object.freeze({ ...(options.queries ?? {}) });
      globalDefaults.mutations = Object.freeze({ ...(options.mutations ?? {}) });
    },
    getDefaultOptions() { return Object.freeze({ ...globalDefaults }); },
    setQueryDefaults(queryKey, options) { queryDefaults.push({ queryKey: normalizeQueryKey(queryKey), options: Object.freeze({ ...options }) }); },
    getQueryDefaults(queryKey) { return Object.freeze({ ...globalDefaults.queries, ...matchingDefaults(queryDefaults, normalizeQueryKey(queryKey)) }); },
    setMutationDefaults(mutationKey, options) { mutationDefaults.push({ queryKey: normalizeQueryKey(mutationKey), options: Object.freeze({ ...options }) }); },
    getMutationDefaults(mutationKey) { return Object.freeze({ ...globalDefaults.mutations, ...matchingDefaults(mutationDefaults, normalizeQueryKey(mutationKey)) }); },
    getQueryCache() { return queryCache; },
    getMutationCache() { return mutationCache; },
    clear() { client.removeQueries(); mutationCache.clear(); },
    async resumePausedMutations() {},
    resourceKey(definition, parameters) {
      if (!definition || typeof definition.queryKey !== "function") throw new TypeError("resourceKey requires a compiled resource definition");
      return definition.queryKey(parameters);
    },
    getResourceData(definition, parameters) { return client.getQueryData(client.resourceKey(definition, parameters)); },
    setResourceData(definition, parameters, updater) { return client.setQueryData(client.resourceKey(definition, parameters), updater); },
    invalidateResource(definition, parameters, options) {
      const queryKey = parameters === undefined ? Object.freeze([definition.id]) : client.resourceKey(definition, parameters);
      return client.invalidateQueries({ queryKey, exact: parameters !== undefined }, options);
    },
    refetchResource(definition, parameters, options) {
      const queryKey = parameters === undefined ? Object.freeze([definition.id]) : client.resourceKey(definition, parameters);
      return client.refetchQueries({ queryKey, exact: parameters !== undefined }, options);
    },
  };

  Object.defineProperty(client, queryClientResourceBridge, {
    value: Object.freeze({
      attach(queryKey, observer, options = {}) {
        const entry = ensureEntry(queryKey, options);
        entry.observers.add(observer);
        return () => { entry.observers.delete(observer); scheduleGc(entry); };
      },
      register(resourceId, validate) {
        if (resourceValidators.get(resourceId) === validate) return;
        resourceValidators.set(resourceId, validate);
        for (const entry of entries.values()) {
          if (entry.queryKey[0] === resourceId && entry.hasData) entry.data = validate(entry.data);
        }
      },
      read(queryKey) { return entries.get(hash(queryKey)); },
      write(queryKey, value, updatedAt) { return write(ensureEntry(queryKey), value, updatedAt, "updated", false); },
      begin(queryKey, token) {
        const entry = ensureEntry(queryKey);
        entry.fetchTokens.add(token);
        emitQuery(entry, "fetching");
      },
      end(queryKey, token) {
        const entry = entries.get(hash(queryKey));
        if (!entry) return;
        entry.fetchTokens.delete(token);
        emitQuery(entry, "settled");
        scheduleGc(entry);
      },
    }),
  });
  emitDevtools("query-client.created", { semanticId: id, runtimeId });
  return Object.freeze(client);
}

function retryDelay(milliseconds, signal) {
  if (milliseconds <= 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError(signal.reason));
      return;
    }
    const timer = setTimeout(finish, milliseconds);
    function finish() {
      signal.removeEventListener("abort", cancel);
      resolve();
    }
    function cancel() {
      clearTimeout(timer);
      signal.removeEventListener("abort", cancel);
      reject(abortError(signal.reason));
    }
    signal.addEventListener("abort", cancel, { once: true });
  });
}

function createResourceInstance(config, input, options, coordinator) {
  const owner = options.owner ?? currentOwner;
  if (!owner && options.detached !== true) {
    throw resourceFailure(
      "RESOURCE_OWNER_REQUIRED",
      `${config.id} requires an Owner; pass { owner } or explicitly opt into { detached: true }`,
      config,
    );
  }
  if (owner?.disposed) {
    throw resourceFailure("RESOURCE_OWNER_DISPOSED", `${config.id} cannot attach to a disposed Owner`, config);
  }

  const parameters = normalizeResourceParameters(config, input);
  const url = buildResourceUrl(config, parameters);
  const cacheKey = JSON.stringify(config.parameters.map((parameter) => parameters[parameter.name]));
  const queryKey = Object.freeze([config.id, ...config.parameters.map((parameter) => parameters[parameter.name])]);
  const queryClient = options.queryClient ?? config.queryClient;
  const queryBridge = queryClient[queryClientResourceBridge];
  queryBridge.register(config.id, config.validate);
  const initialQueryEntry = queryBridge.read(queryKey);
  const initialNow = typeof options.now === "function" ? options.now() : Date.now();
  // Capability-protected cache entries stay Idle until their authorizer has
  // run; unguarded compiler-owned data may expose its synchronous cache state.
  const initialCached = config.capabilities.length === 0 && config.cacheMilliseconds !== null && config.cacheMilliseconds > 0 && initialQueryEntry?.hasData
    ? { data: initialQueryEntry.data, expiresAt: initialQueryEntry.dataUpdatedAt + config.cacheMilliseconds, invalidated: initialQueryEntry.invalidated }
    : null;
  const initialFresh = initialCached && !initialCached.invalidated && initialCached.expiresAt > initialNow;
  const state = signal(
    initialCached
      ? resourceState(initialFresh ? "Ready" : "Refreshing", { data: initialCached.data })
      : resourceState("Idle"),
  );
  const runtimeId = nextRuntimeId("resource");
  let active = null;
  let disposed = false;
  let invalidationGeneration = 0;
  let completedInvalidationGeneration = 0;
  let trailingRefreshScheduled = false;

  function scheduleTrailingRefresh() {
    if (disposed || trailingRefreshScheduled || invalidationGeneration <= completedInvalidationGeneration) return;
    trailingRefreshScheduled = true;
    queueMicrotask(() => {
      trailingRefreshScheduled = false;
      if (disposed || active || invalidationGeneration <= completedInvalidationGeneration) return;
      Promise.resolve(load({ force: true })).catch(() => {});
    });
  }

  function publish(next, payload = {}) {
    if (disposed) return;
    state.set(next);
    emitDevtools("resource.state", {
      semanticId: config.id,
      runtimeId,
      ownerId: owner?.id ?? null,
      payload: { state: next.tag, ...payload },
    });
  }

  async function authorize() {
    if (config.capabilities.length === 0) return;
    if (typeof options.authorize !== "function") {
      throw resourceFailure(
        "RESOURCE_AUTHORIZER_REQUIRED",
        `${config.id} declares capabilities but no authorizer was provided`,
        config,
      );
    }
    for (const capability of config.capabilities) {
      const allowed = await options.authorize(capability, config.id);
      emitDevtools("resource.capability", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { capability, allowed: allowed === true },
      });
      if (allowed !== true) {
        throw resourceFailure(
          "RESOURCE_CAPABILITY_DENIED",
          `${config.id} was denied ${capability}`,
          config,
          { capability },
        );
      }
    }
  }

  async function request(controller, requestId) {
    const fetcher = options.fetch ?? globalThis.fetch;
    if (typeof fetcher !== "function") {
      throw resourceFailure("RESOURCE_FETCH_UNAVAILABLE", `No fetch implementation is available for ${config.id}`, config);
    }
    const maxAttempts = config.retry + 1;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      emitDevtools("resource.request.started", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { requestId, request: config.request.id, method: config.request.method, url, attempt },
      });
      let failure;
      try {
        const response = await fetcher(url, { method: config.request.method, signal: controller.signal });
        if (!response || typeof response.ok !== "boolean") {
          throw resourceFailure("RESOURCE_RESPONSE_INVALID", `${config.id} received an invalid fetch response`, config);
        }
        if (!response.ok) {
          const status = Number.isInteger(response.status) ? response.status : null;
          throw resourceFailure(
            "RESOURCE_HTTP_ERROR",
            `${config.id} request failed with HTTP ${status ?? "unknown"}`,
            config,
            { status, attempt, retriable: status === 429 || (status !== null && status >= 500) },
          );
        }
        let untrusted;
        try {
          untrusted = await response.json();
        } catch (error) {
          throw resourceFailure(
            "RESOURCE_RESPONSE_PARSE_FAILED",
            `${config.id} response was not valid JSON`,
            config,
            { attempt, cause: error },
          );
        }
        if (controller.signal.aborted) throw abortError(controller.signal.reason);
        emitDevtools("resource.validation.started", {
          semanticId: config.id,
          runtimeId,
          ownerId: owner?.id ?? null,
          payload: { requestId, boundary: config.boundary },
        });
        const trusted = config.validate(untrusted);
        emitDevtools("resource.validation.succeeded", {
          semanticId: config.id,
          runtimeId,
          ownerId: owner?.id ?? null,
          payload: { requestId, boundary: config.boundary },
        });
        return trusted;
      } catch (error) {
        if (isAbort(error, controller.signal)) throw abortError(controller.signal.reason);
        failure = error instanceof ResourceExecutionError || error?.code === "EXTERNAL_VALIDATION_FAILED"
          ? error
          : resourceFailure(
              "RESOURCE_NETWORK_ERROR",
              `${config.id} network request failed`,
              config,
              { attempt, cause: error, retriable: true },
            );
        if (failure.code === "EXTERNAL_VALIDATION_FAILED") {
          emitDevtools("resource.validation.failed", {
            semanticId: config.id,
            runtimeId,
            ownerId: owner?.id ?? null,
            payload: { requestId, boundary: config.boundary, code: failure.code, path: failure.path ?? [] },
          });
        }
      }
      if (!failure.retriable || attempt === maxAttempts) throw failure;
      const milliseconds = typeof options.retryDelay === "function"
        ? options.retryDelay(attempt, failure)
        : Math.min(1000 * (2 ** (attempt - 1)), 30000);
      emitDevtools("resource.request.retrying", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { requestId, attempt, nextAttempt: attempt + 1, delayMilliseconds: milliseconds, code: failure.code },
      });
      await retryDelay(milliseconds, controller.signal);
    }
    throw resourceFailure("RESOURCE_REQUEST_FAILED", `${config.id} exhausted its retry policy`, config);
  }

  function subscribeToRequest() {
    let entry = coordinator.inflight.get(cacheKey);
    const joined = entry !== undefined;
    if (!entry) {
      const controller = new AbortController();
      const requestId = nextRuntimeId("resource-request");
      entry = {
        controller,
        requestId,
        creatorRuntimeId: runtimeId,
        subscribers: new Map(),
        settled: false,
        promise: null,
      };
      coordinator.inflight.set(cacheKey, entry);
      emitDevtools("resource.request.created", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { requestId, cacheKey, url },
      });
      entry.promise = request(controller, requestId).then(
        (value) => {
          entry.settled = true;
          if (coordinator.inflight.get(cacheKey) === entry) coordinator.inflight.delete(cacheKey);
          return value;
        },
        (error) => {
          entry.settled = true;
          if (coordinator.inflight.get(cacheKey) === entry) coordinator.inflight.delete(cacheKey);
          throw error;
        },
      );
    }

    const subscriberId = nextRuntimeId("resource-subscriber");
    let released = false;
    let rejectSubscriber;
    const promise = new Promise((resolve, reject) => {
      rejectSubscriber = reject;
      entry.promise.then(
        (value) => { if (!released) resolve(value); },
        (error) => { if (!released) reject(error); },
      );
    });
    entry.subscribers.set(subscriberId, runtimeId);
    if (joined) {
      emitDevtools("resource.request.joined", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: {
          requestId: entry.requestId,
          cacheKey,
          consumers: entry.subscribers.size,
          creatorRuntimeId: entry.creatorRuntimeId,
        },
      });
    }

    function release(reason = "Resource request subscription released", rejectLocal = true) {
      if (released) return;
      released = true;
      entry.subscribers.delete(subscriberId);
      if (rejectLocal) rejectSubscriber(abortError(reason));
      emitDevtools("resource.request.released", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { requestId: entry.requestId, cacheKey, consumers: entry.subscribers.size, reason },
      });
      if (!entry.settled && entry.subscribers.size === 0) {
        if (coordinator.inflight.get(cacheKey) === entry) coordinator.inflight.delete(cacheKey);
        emitDevtools("resource.request.aborted", {
          semanticId: config.id,
          runtimeId,
          ownerId: owner?.id ?? null,
          payload: { requestId: entry.requestId, cacheKey, reason },
        });
        entry.controller.abort(reason);
      }
    }

    return { entry, joined, promise, release, subscriberId };
  }

  async function load(loadOptions = {}) {
    if (disposed) throw resourceFailure("RESOURCE_DISPOSED", `${config.id} instance is disposed`, config);
    try {
      await authorize();
    } catch (error) {
      if (!disposed) {
        publish(resourceState("Failed", { error, data: null }), { code: error?.code ?? "RESOURCE_AUTHORIZATION_FAILED" });
        emitDevtools("resource.request.failed", {
          semanticId: config.id,
          runtimeId,
          ownerId: owner?.id ?? null,
          payload: { requestId: null, code: error?.code ?? "RESOURCE_AUTHORIZATION_FAILED" },
        });
      }
      throw error;
    }
    if (disposed) throw abortError("Resource owner was disposed during authorization");
    const force = loadOptions.force === true;
    const policy = force
      ? "network_first"
      : config.reloadPolicy === "manual"
        ? "cache_first"
        : config.reloadPolicy;
    const now = typeof options.now === "function" ? options.now() : Date.now();
    const queryEntry = queryBridge.read(queryKey);
    const cached = config.cacheMilliseconds !== null && config.cacheMilliseconds > 0 && queryEntry?.hasData
      ? { data: queryEntry.data, expiresAt: queryEntry.dataUpdatedAt + config.cacheMilliseconds, invalidated: queryEntry.invalidated }
      : null;
    const cacheFresh = cached && !cached.invalidated && cached.expiresAt > now;
    if (policy === "cache_first" && cacheFresh) {
      publish(resourceState("Ready", { data: cached.data }), { cached: true });
      emitDevtools("resource.cache.hit", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { expiresAt: cached.expiresAt },
      });
      return cached.data;
    }
    if (policy === "cache_first" && cached && !cacheFresh) {
      emitDevtools("resource.cache.expired", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { expiredAt: cached.expiresAt },
      });
    }
    if (policy === "network_first" && cached) {
      emitDevtools("resource.cache.bypassed", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { force, fresh: cacheFresh },
      });
    }

    if (active) return active.promise;
    const loadInvalidationGeneration = invalidationGeneration;
    const previousState = state.get();
    let previousData = previousState.tag === "Ready" || previousState.tag === "Refreshing"
      ? previousState.data
      : null;
    if (policy === "stale_while_revalidate" && cached) {
      previousData = cached.data;
      publish(resourceState("Ready", { data: cached.data }), { cached: true, stale: !cacheFresh });
      emitDevtools("resource.cache.revalidating", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { fresh: cacheFresh, expiresAt: cached.expiresAt },
      });
    }
    publish(
      previousData === null ? resourceState("Loading") : resourceState("Refreshing", { data: previousData }),
      { cached: false, reloadPolicy: policy },
    );
    const subscription = subscribeToRequest();
    const requestId = subscription.entry.requestId;
    queryBridge.begin(queryKey, subscription.subscriberId);
    active = subscription;
    try {
      const trusted = await subscription.promise;
      if (disposed || active?.subscriberId !== subscription.subscriberId) {
        throw abortError("Stale resource request");
      }
      const completedAt = typeof options.now === "function" ? options.now() : Date.now();
      queryBridge.write(queryKey, trusted, completedAt);
      publish(resourceState("Ready", { data: trusted }), { cached: false, shared: subscription.joined });
      emitDevtools("resource.request.succeeded", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { requestId, shared: subscription.joined },
      });
      return trusted;
    } catch (error) {
      if (error?.code === "RESOURCE_ABORTED") {
        emitDevtools("resource.request.cancelled", {
          semanticId: config.id,
          runtimeId,
          ownerId: owner?.id ?? null,
          payload: { requestId },
        });
        if (!disposed && active?.subscriberId === subscription.subscriberId) {
          publish(previousData === null ? resourceState("Idle") : resourceState("Ready", { data: previousData }), { cancelled: true });
        }
        throw error;
      }
      if (!disposed && active?.subscriberId === subscription.subscriberId) {
        publish(resourceState("Failed", { error, data: previousData }), { code: error?.code ?? "RESOURCE_REQUEST_FAILED" });
        emitDevtools("resource.request.failed", {
          semanticId: config.id,
          runtimeId,
          ownerId: owner?.id ?? null,
          payload: { requestId, code: error?.code ?? "RESOURCE_REQUEST_FAILED" },
        });
      }
      throw error;
    } finally {
      queryBridge.end(queryKey, subscription.subscriberId);
      subscription.release("Resource request subscription completed", false);
      if (active?.subscriberId === subscription.subscriberId) active = null;
      // A failed invalidation-triggered request has still consumed the
      // generation it began with. Only a newer invalidation earns one
      // trailing request; retry remains owned by the declared resource policy.
      completedInvalidationGeneration = Math.max(completedInvalidationGeneration, loadInvalidationGeneration);
      scheduleTrailingRefresh();
    }
  }

  function cancel(reason = "Resource request was cancelled") {
    if (active) active.release(reason);
  }

  function clearCache() {
    queryClient.removeQueries({ queryKey, exact: true });
    emitDevtools("resource.cache.cleared", {
      semanticId: config.id,
      runtimeId,
      ownerId: owner?.id ?? null,
      payload: { cacheKey },
    });
  }

  emitDevtools("resource.created", {
    semanticId: config.id,
    runtimeId,
    ownerId: owner?.id ?? null,
    payload: { state: "Idle", url },
  });
  const detachQuery = queryBridge.attach(queryKey, {
    changed(queryState, kind) {
      if (disposed || kind === "fetching" || kind === "settled" || kind === "removed") return;
      if (queryState.data !== undefined) {
        const current = state.get();
        publish(
          kind === "invalidated" || current.tag === "Loading" || current.tag === "Refreshing"
            ? resourceState("Refreshing", { data: queryState.data })
            : resourceState("Ready", { data: queryState.data }),
          { queryClient: true, kind },
        );
      }
    },
    refetch(queryOptions) { return load({ force: true, ...queryOptions }); },
    invalidated(data) {
      invalidationGeneration += 1;
      if (data !== undefined) publish(resourceState("Refreshing", { data }), { queryClient: true, kind: "invalidated" });
      scheduleTrailingRefresh();
    },
    cancel,
    reset(data) { publish(data === undefined ? resourceState("Idle") : resourceState("Ready", { data }), { queryClient: true, reset: true }); },
  }, { gcTime: config.cacheMilliseconds ?? 0 });
  if (owner) {
    onCleanup(owner, () => {
      disposed = true;
      detachQuery();
      if (active) active.release("Resource owner was disposed");
      emitDevtools("resource.disposed", { semanticId: config.id, runtimeId, ownerId: owner.id });
    });
  }

  return Object.freeze({
    id: config.id,
    runtimeId,
    parameters,
    queryKey,
    queryClient,
    state,
    load,
    refresh() { return load({ force: true }); },
    cancel,
    clearCache,
  });
}

export function acquireResource(
  definition,
  readParameters,
  owner = currentOwner,
  sources = null,
  options = {},
  semanticId = definition?.id ?? null,
) {
  if (!owner) {
    throw new ResourceExecutionError(
      "RESOURCE_OWNER_REQUIRED",
      `${semanticId ?? "resource acquisition"} requires an Owner`,
      { resource: definition?.id ?? null },
    );
  }
  if (typeof readParameters !== "function") throw new TypeError("Resource acquisition requires a parameter reader");
  const state = signal(resourceState("Idle"));
  const runtimeId = nextRuntimeId("resource-acquisition");
  if (isScenarioStub(options, semanticId)) {
    // `noxid test` supplies a closed set of compiler-owned acquisition IDs.
    // A stubbed acquisition never reads parameters, creates a resource
    // instance, authorizes, or performs network I/O. The scenario harness
    // drives this ordinary signal with a semantics-validated lifecycle value.
    let seededAgeMilliseconds = 0;
    const handle = Object.freeze({
      id: semanticId,
      definitionId: definition.id,
      runtimeId,
      state,
      load() { return Promise.resolve(state.get()); },
      refresh() { return Promise.resolve(state.get()); },
      cancel() {},
      clearCache() {},
      seedScenario(lifecycle, ageMilliseconds = 0) {
        if (!lifecycle || typeof lifecycle.tag !== "string") {
          throw new TypeError("Scenario resource lifecycle must be a tagged value");
        }
        if (ageMilliseconds === null) ageMilliseconds = 0;
        if (!Number.isFinite(ageMilliseconds) || ageMilliseconds < 0) {
          throw new RangeError("Scenario resource age must be a non-negative duration in milliseconds");
        }
        seededAgeMilliseconds = ageMilliseconds;
        const stale = definition.cacheMilliseconds !== null
          && ageMilliseconds >= definition.cacheMilliseconds
          && (lifecycle.tag === "Ready" || lifecycle.tag === "Refreshing");
        state.set(stale ? Object.freeze({ ...lifecycle, tag: "Refreshing" }) : lifecycle);
      },
      get scenarioAgeMilliseconds() { return seededAgeMilliseconds; },
    });
    let handles = options.scenarioResourceHandles.get(definition.id);
    if (!handles) {
      handles = new Set();
      options.scenarioResourceHandles.set(definition.id, handles);
    }
    handles.add(handle);
    onCleanup(owner, () => handles.delete(handle));
    return handle;
  }
  let current = null;
  let disposed = false;
  let acquisitionCount = 0;
  let parameterKey = null;
  let seededSnapshot = false;
  let unregisterLive = null;

  if (definition.live === true) {
    const liveController = options.liveResourceController ?? __noxidInstalledLiveResourceController;
    if (!liveController || typeof liveController.register !== "function") {
      throw new ResourceExecutionError(
        "LIVE_RESOURCE_CONTROLLER_REQUIRED",
        `${semanticId ?? definition.id} is live but the compiler-owned project connection is unavailable`,
        { resource: definition.id },
      );
    }
    unregisterLive = liveController.register(
      definition,
      options.queryClient ?? definition.queryClient,
      options.route?.id,
      options.route?.path,
    );
  }

  function start() {
    if (disposed) return;
    const parameters = readParameters();
    const nextParameterKey = stableAcquisitionKey(parameters);
    if (current && nextParameterKey === parameterKey) {
      emitDevtools("resource.acquisition.skipped", {
        semanticId, runtimeId, ownerId: owner.id,
        payload: { resource: definition.id, reason: "parameters_unchanged" },
      });
      return;
    }
    const reason = acquisitionCount === 0 ? "mount" : "arguments_changed";
    acquisitionCount += 1;
    const snapshotGroup = !seededSnapshot && Object.prototype.hasOwnProperty.call(options.resourceSnapshots ?? {}, semanticId)
      ? options.resourceSnapshots[semanticId]
      : null;
    const snapshotCandidates = Array.isArray(snapshotGroup)
      ? snapshotGroup
      : snapshotGroup ? [snapshotGroup] : [];
    const snapshot = snapshotCandidates.find((candidate) => stableAcquisitionKey(candidate?.parameters) === nextParameterKey) ?? null;
    if (!seededSnapshot && snapshotCandidates.length > 0 && !snapshot) {
      throw new ResourceExecutionError(
        "RESOURCE_SNAPSHOT_PARAMETERS_INVALID",
        `Hydration snapshot parameters do not match ${semanticId}`,
        { resource: definition.id },
      );
    }
    if (snapshot) {
      if (snapshot.acquisitionId !== semanticId || snapshot.resourceId !== definition.id) {
        throw new ResourceExecutionError(
          "RESOURCE_SNAPSHOT_IDENTITY_INVALID",
          `Hydration snapshot identity does not match ${semanticId}`,
          { resource: definition.id },
        );
      }
      definition.seed(snapshot.parameters, snapshot.data, snapshot.renderedAt);
      seededSnapshot = true;
    }
    if (current) {
      disposeOwner(current.owner);
      current = null;
    }
    state.set(resourceState("Idle"));
    const resourceOwner = createOwner(owner);
    const instance = definition.create(parameters, { ...options, owner: resourceOwner });
    parameterKey = nextParameterKey;
    current = { owner: resourceOwner, instance };
    effect(() => state.set(instance.state.get()), resourceOwner, [instance.state]);
    emitDevtools("resource.acquired", {
      semanticId,
      runtimeId,
      ownerId: owner.id,
      payload: {
        resource: definition.id,
        instanceRuntimeId: instance.runtimeId,
        reloadPolicy: definition.reloadPolicy,
        autoLoad: definition.reloadPolicy !== "manual",
        reason,
      },
    });
    if (definition.reloadPolicy !== "manual") {
      instance.load().catch((error) => {
        if (error?.code !== "RESOURCE_ABORTED" && current?.instance === instance) {
          // The inner resource has already published its structured Failed state.
        }
      });
    }
  }

  effect(start, owner, sources);
  onCleanup(owner, () => {
    disposed = true;
    current = null;
    unregisterLive?.();
    unregisterLive = null;
    emitDevtools("resource.acquisition.disposed", {
      semanticId,
      runtimeId,
      ownerId: owner.id,
    });
  });

  function active() {
    if (!current) {
      throw new ResourceExecutionError(
        "RESOURCE_NOT_ACQUIRED",
        `${semanticId ?? definition.id} has no active resource instance`,
        { resource: definition.id },
      );
    }
    return current.instance;
  }

  return Object.freeze({
    id: semanticId,
    runtimeId,
    state,
    load() { return active().load(); },
    refresh() { return active().refresh(); },
    cancel(reason) { return active().cancel(reason); },
    clearCache() { return active().clearCache(); },
  });
}

export function createResourceDefinition(config) {
  if (!config || typeof config !== "object") throw new TypeError("Resource definition must be an object");
  if (typeof config.id !== "string" || !config.request || !Array.isArray(config.parameters)) {
    throw new TypeError("Resource definition is missing compiler-owned identity, request, or parameters");
  }
  if (typeof config.validate !== "function") {
    throw resourceFailure("RESOURCE_VALIDATOR_REQUIRED", `${config.id} has no generated boundary validator`, config);
  }
  const normalized = Object.freeze({
    ...config,
    queryClient: config.queryClient ?? createQueryClient({ id: `${config.id}.query-client` }),
    retry: Number.isInteger(config.retry) && config.retry >= 0 ? config.retry : 0,
    cacheMilliseconds: Number.isFinite(config.cacheMilliseconds) ? config.cacheMilliseconds : null,
    reloadPolicy: ["cache_first", "network_first", "stale_while_revalidate", "manual"].includes(config.reloadPolicy)
      ? config.reloadPolicy
      : "cache_first",
    capabilities: Object.freeze([...(config.capabilities ?? [])]),
  });
  const coordinator = { inflight: new Map() };
  normalized.queryClient[queryClientResourceBridge].register(normalized.id, normalized.validate);
  return Object.freeze({
    id: normalized.id,
    cacheMilliseconds: normalized.cacheMilliseconds,
    refresh: Object.freeze([...(normalized.refresh ?? [])]),
    reloadPolicy: normalized.reloadPolicy,
    live: normalized.live === true,
    queryClient: normalized.queryClient,
    queryKey(parameters) {
      const normalizedParameters = normalizeResourceParameters(normalized, parameters);
      return Object.freeze([normalized.id, ...normalized.parameters.map((parameter) => normalizedParameters[parameter.name])]);
    },
    create(parameters, options = {}) {
      return createResourceInstance(normalized, parameters, options, coordinator);
    },
    seed(parameters, data, updatedAt) {
      const normalizedParameters = normalizeResourceParameters(normalized, parameters);
      const queryKey = Object.freeze([normalized.id, ...normalized.parameters.map((parameter) => normalizedParameters[parameter.name])]);
      if (!Number.isFinite(updatedAt) || updatedAt < 0) throw new TypeError(`${normalized.id} seed updatedAt must be a non-negative timestamp`);
      const trusted = normalized.validate(data);
      normalized.queryClient[queryClientResourceBridge].write(queryKey, trusted, updatedAt);
      return trusted;
    },
    async prefetch(parameters, options = {}) {
      const owner = createOwner(options.owner ?? null, { semanticId: normalized.id, label: "resource-prefetch" });
      try {
        return await createResourceInstance(normalized, parameters, { ...options, owner }, coordinator).load();
      } finally {
        disposeOwner(owner);
      }
    },
    clearCache() { normalized.queryClient.removeQueries({ queryKey: [normalized.id] }); },
  });
}

const LIVE_RESOURCE_EVENT_LIMIT_BYTES = 65536;
const LIVE_RESOURCE_FRAME_LIMIT_BYTES = 70000;
const LIVE_RESOURCE_MAX_CURSOR_SETS = 32;
let __noxidInstalledLiveResourceController = null;

function presenceCredential(label) {
  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    globalThis.crypto.getRandomValues(bytes);
    return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  throw liveResourceFailure("PRESENCE_CRYPTO_UNAVAILABLE", `Secure randomness is required to create a presence ${label}`);
}

function liveResourceEventId(value) {
  if (typeof value !== "string" || value.length > 256) return null;
  const match = /^([A-Za-z0-9_-]{16,128}):([1-9][0-9]{0,15})$/.exec(value);
  if (match === null) return null;
  return Number.isSafeInteger(Number(match[2])) ? value : null;
}

function liveResourceFailure(code, message, cause = undefined) {
  const error = new Error(message);
  error.name = "LiveResourceConnectionError";
  error.code = code;
  if (cause !== undefined) error.cause = cause;
  return error;
}

function decodeLiveResourceFrame(lines, resourceManifest, resourceSubscriptions, presenceManifest, presenceSubscriptions) {
  let event = "message";
  let id = null;
  const data = [];
  for (const line of lines) {
    if (line === "" || line.startsWith(":")) continue;
    const separator = line.indexOf(":");
    const field = separator < 0 ? line : line.slice(0, separator);
    let value = separator < 0 ? "" : line.slice(separator + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
    else if (field === "id" && !value.includes("\0")) id = value;
  }
  if (data.length === 0) return null;
  const encoded = data.join("\n");
  if (new TextEncoder().encode(encoded).byteLength > LIVE_RESOURCE_EVENT_LIMIT_BYTES) {
    throw liveResourceFailure("LIVE_RESOURCE_EVENT_TOO_LARGE", "Live invalidation exceeded the compiler-owned event limit");
  }
  if (event === "noxid-live-reset" && encoded === "null") return Object.freeze({ reset: true, sync: false });
  if (event === "noxid-live-sync" && encoded === "null" && id === null) return Object.freeze({ reset: false, sync: true });
  if (event === "noxid-presence-event") {
    let payload;
    try { payload = JSON.parse(encoded); }
    catch (cause) { throw liveResourceFailure("PRESENCE_EVENT_JSON_INVALID", "Presence delivery was not valid JSON", cause); }
    if (!payload || typeof payload !== "object" || typeof payload.presence !== "string"
      || !presenceManifest.has(payload.presence) || !presenceSubscriptions.has(payload.presence)
      || !payload.event || typeof payload.event !== "object" || !["Snapshot", "Joined", "Updated", "Left"].includes(payload.event.tag)) {
      throw liveResourceFailure("PRESENCE_EVENT_CONTRACT_INVALID", "Presence delivery did not match the compiler-owned subscription and event algebra");
    }
    return Object.freeze({ reset: false, sync: false, presence: payload.presence, event: payload.event });
  }
  if (event !== "noxid-live-invalidation") {
    throw liveResourceFailure("LIVE_RESOURCE_EVENT_UNKNOWN", "Live connection emitted an unsupported SSE event");
  }
  if (liveResourceEventId(id) === null) {
    throw liveResourceFailure("LIVE_RESOURCE_EVENT_ID_INVALID", "Live invalidation omitted a valid compiler-owned cursor");
  }
  let semanticId;
  try { semanticId = JSON.parse(encoded); }
  catch (cause) { throw liveResourceFailure("LIVE_RESOURCE_EVENT_JSON_INVALID", "Live invalidation was not valid JSON", cause); }
  if (typeof semanticId !== "string" || !resourceManifest.has(semanticId) || !resourceSubscriptions.has(semanticId)) {
    throw liveResourceFailure("LIVE_RESOURCE_EVENT_IDENTITY_INVALID", "Live invalidation named a resource outside the compiled subscription set");
  }
  return Object.freeze({ reset: false, sync: false, id, semanticId });
}

export function createLiveResourceController(input) {
  if (!input || typeof input !== "object" || !Array.isArray(input.resources ?? []) || !Array.isArray(input.presences ?? [])) {
    throw new TypeError("Live controller requires compiler-owned resource and presence manifests");
  }
  const manifestValues = [...(input.resources ?? [])];
  const presenceManifestValues = [...(input.presences ?? [])];
  const websocketPresenceValues = [...(input.websocketPresences ?? [])];
  if ((manifestValues.length === 0 && presenceManifestValues.length === 0)
    || manifestValues.some((id) => typeof id !== "string" || !/^resource:[A-Za-z_][A-Za-z0-9_]*$/.test(id))
    || new Set(manifestValues).size !== manifestValues.length) {
    throw new TypeError("Live controller manifest requires unique semantic resource identities");
  }
  if (presenceManifestValues.some((id) => typeof id !== "string" || !/^presence:[A-Za-z_][A-Za-z0-9_]*$/.test(id))
    || new Set(presenceManifestValues).size !== presenceManifestValues.length) {
    throw new TypeError("Live controller manifest requires unique semantic presence identities");
  }
  if (websocketPresenceValues.some((id) => !presenceManifestValues.includes(id))
    || new Set(websocketPresenceValues).size !== websocketPresenceValues.length) {
    throw new TypeError("WebSocket presence manifest must be a unique subset of compiler-owned presence identities");
  }
  const manifest = new Set(manifestValues);
  const presenceManifest = new Set(presenceManifestValues);
  const websocketPresenceManifest = new Set(websocketPresenceValues);
  const basePath = input.basePath === "/" ? "" : String(input.basePath ?? "").replace(/\/$/, "");
  const fetcher = input.fetch ?? globalThis.fetch;
  const WebSocketConstructor = input.WebSocket ?? globalThis.WebSocket;
  if (typeof fetcher !== "function") throw new TypeError("Live resource controller requires fetch");
  const retryMilliseconds = Number.isFinite(input.retryMilliseconds) && input.retryMilliseconds >= 0
    ? input.retryMilliseconds
    : 1000;
  const registrations = new Map();
  const presenceRegistrations = new Map();
  const cursors = new Map();
  const pending = new Map();
  const websocketPresenceWrites = new Map();
  let draining = false;
  let stopped = false;
  let desiredVersion = 0;
  let cycleController = null;
  let supervisor = null;
  let reconciliationScheduled = false;
  let nextRegistrationOrdinal = 0;
  let nextPresenceWriteOrdinal = 0;

  function presenceWriteKey(presenceId, routeId, routePath) {
    return `${routeId}\n${routePath}\n${presenceId}`;
  }

  function rememberCursor(key, value) {
    cursors.delete(key);
    cursors.set(key, value);
    while (cursors.size > LIVE_RESOURCE_MAX_CURSOR_SETS) cursors.delete(cursors.keys().next().value);
  }

  function activeConfiguration() {
    let selected = null;
    for (const entries of registrations.values()) {
      for (const registration of entries) {
        if (selected === null || registration.ordinal > selected.ordinal) selected = registration;
      }
    }
    for (const entries of presenceRegistrations.values()) {
      for (const registration of entries) {
        if (selected === null || registration.ordinal > selected.ordinal) selected = registration;
      }
    }
    if (selected === null) return null;
    const routeId = selected.routeId;
    const routePath = selected.routePath;
    const ids = [...registrations.entries()]
      .filter(([, entries]) => [...entries].some((registration) => registration.routeId === routeId && registration.routePath === routePath))
      .map(([id]) => id)
      .sort();
    const allPresenceIds = [...presenceRegistrations.entries()]
      .filter(([, entries]) => [...entries].some((registration) => registration.routeId === routeId && registration.routePath === routePath))
      .map(([id]) => id)
      .sort();
    const presenceIds = allPresenceIds.filter((id) => !websocketPresenceManifest.has(id));
    const websocketPresenceIds = allPresenceIds.filter((id) => websocketPresenceManifest.has(id));
    return Object.freeze({
      routeId,
      routePath,
      ids: Object.freeze(ids),
      presenceIds: Object.freeze(presenceIds),
      websocketPresenceIds: Object.freeze(websocketPresenceIds),
      key: `${routeId}\n${routePath}\nresources:${ids.join("\n")}\npresences:${presenceIds.join("\n")}`,
      websocketKey: `${routeId}\n${routePath}\nwebsocket-presences:${websocketPresenceIds.join("\n")}`,
    });
  }

  async function invalidate(semanticId, routeId, routePath) {
    const entries = [...(registrations.get(semanticId) ?? [])]
      .filter((entry) => entry.routeId === routeId && entry.routePath === routePath);
    const seen = new Map();
    for (const entry of entries) {
      let definitions = seen.get(entry.queryClient);
      if (!definitions) { definitions = new Set(); seen.set(entry.queryClient, definitions); }
      definitions.add(entry.definition);
    }
    await Promise.allSettled([...seen].flatMap(([queryClient, definitions]) =>
      [...definitions].map((definition) => queryClient.invalidateResource(definition))
    ));
  }

  function scheduleDrain() {
    if (draining || stopped) return;
    draining = true;
    queueMicrotask(async () => {
      try {
        while (!stopped && pending.size > 0) {
          const batch = [...pending.values()];
          pending.clear();
          await Promise.allSettled(batch.map((entry) => invalidate(entry.semanticId, entry.routeId, entry.routePath)));
        }
      } finally {
        draining = false;
        if (!stopped && pending.size > 0) scheduleDrain();
      }
    });
  }

  function enqueue(semanticId, routeId, routePath) {
    if (stopped || !registrations.has(semanticId)) return;
    const key = `${routeId}\n${routePath}\n${semanticId}`;
    pending.set(key, Object.freeze({ semanticId, routeId, routePath }));
    scheduleDrain();
  }

  function invalidateAll(ids, routeId, routePath) {
    for (const semanticId of ids) enqueue(semanticId, routeId, routePath);
  }

  const presenceSetInterval = input.setInterval ?? globalThis.setInterval;
  const presenceClearInterval = input.clearInterval ?? globalThis.clearInterval;
  const presenceSetTimeout = input.setTimeout ?? globalThis.setTimeout;
  const presenceClearTimeout = input.clearTimeout ?? globalThis.clearTimeout;

  function presenceSource(registration) {
    return Object.freeze({
      [Symbol.asyncIterator]() {
        let waiting = null;
        return Object.freeze({
          next() {
            if (registration.queue.length > 0) return Promise.resolve({ done: false, value: registration.queue.shift() });
            if (registration.failure) return Promise.reject(registration.failure);
            if (registration.closed) return Promise.resolve({ done: true, value: undefined });
            if (waiting !== null) return Promise.reject(liveResourceFailure("PRESENCE_ITERATOR_CONCURRENT_NEXT", "Presence consumers must await each event before requesting another"));
            return new Promise((resolve, reject) => {
              waiting = {
                resolve(value) { waiting = null; resolve(value); },
                reject(error) { waiting = null; reject(error); },
              };
              registration.waiters.push(waiting);
            });
          },
          return() {
            if (waiting !== null) {
              const index = registration.waiters.indexOf(waiting);
              if (index >= 0) registration.waiters.splice(index, 1);
              const waiter = waiting;
              waiting = null;
              waiter.resolve({ done: true, value: undefined });
            }
            return Promise.resolve({ done: true, value: undefined });
          },
        });
      },
    });
  }

  function deliverPresence(registration, event) {
    if (registration.closed) return;
    if (registration.awaitingSnapshot && event?.tag !== "Snapshot") return;
    if (event?.tag === "Snapshot") {
      registration.awaitingSnapshot = false;
      registration.queue = [];
    }
    const waiter = registration.waiters.shift();
    if (waiter) waiter.resolve({ done: false, value: event });
    else {
      if (registration.queue.length >= 64) {
        const memberId = event?.tag === "Updated" ? event.value?.id : null;
        let sameMember = -1;
        if (memberId !== null) {
          for (let index = registration.queue.length - 1; index >= 0; index -= 1) {
            const item = registration.queue[index];
            const structuralMember = item?.tag === "Left" ? item.value : item?.value?.id;
            if ((item?.tag === "Joined" || item?.tag === "Left") && structuralMember === memberId) break;
            if (item?.tag === "Updated" && item.value?.id === memberId) { sameMember = index; break; }
          }
        }
        if (sameMember >= 0) {
          registration.queue[sameMember] = event;
          return;
        }
        registration.queue = [];
        registration.awaitingSnapshot = true;
        reconcile();
        return;
      }
      registration.queue.push(event);
    }
    if (event?.tag === "Snapshot" && !registration.joinStarted) {
      registration.joinStarted = true;
      void joinPresence(registration);
    }
  }

  async function writePresence(registration, operation, record = null, keepalive = false) {
    if (registration.closed && operation !== "leave") return;
    const payload = { presence: registration.contract.id, operation };
    if (operation === "join") {
      payload.nonce = registration.joinNonce;
      payload.token = registration.token;
    }
    if (operation === "join" || operation === "update") payload.record = record;
    if (operation !== "join") {
      payload.memberId = registration.memberId;
      payload.token = registration.token;
    }
    let result = null;
    let response = null;
    const websocketWrite = websocketPresenceWrites.get(presenceWriteKey(registration.contract.id, registration.routeId, registration.routePath));
    if (registration.contract.transport === "websocket" && websocketWrite) {
      try {
        result = await websocketWrite(Object.freeze({
          type: "presence.write",
          requestId: `presence-${++nextPresenceWriteOrdinal}`,
          ...payload,
        }));
        response = { ok: result?.ok === true, status: result?.ok === true ? 200 : 400, headers: { get() { return null; } } };
      } catch (cause) {
        const failure = liveResourceFailure("PRESENCE_WRITE_UNAVAILABLE", `Presence ${operation} WebSocket transport failed`, cause);
        failure.retryable = true;
        throw failure;
      }
    } else {
      try {
        response = await fetcher(`${basePath}/_noxid/presence`, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json", "x-noxid-route-id": registration.routeId, "x-noxid-route-path": registration.routePath },
          body: JSON.stringify(payload),
          keepalive,
        });
      } catch (cause) {
        const failure = liveResourceFailure("PRESENCE_WRITE_UNAVAILABLE", `Presence ${operation} transport failed`, cause);
        failure.retryable = true;
        throw failure;
      }
      try { result = await response?.json(); } catch {}
    }
    const expectedKeys = operation === "join" ? "memberId\nok" : "ok";
    const resultDescriptors = result !== null && typeof result === "object" && !Array.isArray(result)
      ? Object.getOwnPropertyDescriptors(result)
      : null;
    const exactSuccess = response?.ok && result?.ok === true
      && (Object.getPrototypeOf(result) === Object.prototype || Object.getPrototypeOf(result) === null)
      && Object.keys(result).sort().join("\n") === expectedKeys
      && Object.values(resultDescriptors).every((descriptor) => descriptor.enumerable === true && Object.hasOwn(descriptor, "value"));
    if (!exactSuccess) {
      const failure = liveResourceFailure(result?.error?.code ?? "PRESENCE_WRITE_FAILED", result?.error?.message ?? `Presence ${operation} failed`);
      failure.retryable = result?.error?.code === "PRESENCE_MEMBERSHIP_EXPIRED"
        || response?.status === 429
        || response?.status >= 500;
      failure.operation = operation;
      const retryAfter = Number(response?.headers?.get("retry-after"));
      if (response?.status === 429 && Number.isFinite(retryAfter) && retryAfter >= 0) failure.retryAfterMilliseconds = Math.min(60_000, Math.max(1_000, Math.ceil(retryAfter * 1000)));
      throw failure;
    }
    return result;
  }

  function recoverPresence(registration, cause) {
    if (registration.closed) return;
    if (cause?.code === "PRESENCE_RATE_LIMITED" || cause?.operation && cause?.retryable === true && cause?.retryAfterMilliseconds !== undefined) {
      if (registration.retryTimer !== null) return;
      const generation = registration.generation;
      const delay = cause.retryAfterMilliseconds ?? Math.min(60_000, Math.max(1_000, retryMilliseconds));
      registration.retryTimer = presenceSetTimeout?.(() => {
        registration.retryTimer = null;
        if (registration.closed || registration.generation !== generation) return;
        if (cause.operation === "join") void joinPresence(registration);
        else if (cause.operation === "update") void drainPresenceUpdates(registration);
        else if (cause.operation === "heartbeat" && registration.memberId !== null) {
          heartbeatPresence(registration);
        }
      }, delay) ?? null;
      registration.retryTimer?.unref?.();
      return;
    }
    if (cause?.retryable !== true) {
      if (registration.heartbeat !== null) presenceClearInterval?.(registration.heartbeat);
      if (registration.retryTimer !== null) presenceClearTimeout?.(registration.retryTimer);
      registration.heartbeat = null;
      registration.retryTimer = null;
      registration.heartbeatWriting = false;
      registration.generation += 1;
      const memberId = registration.memberId;
      registration.memberId = null;
      registration.fail(cause);
      registration.closed = true;
      const entries = presenceRegistrations.get(registration.contract.id);
      entries?.delete(registration);
      if (entries?.size === 0) presenceRegistrations.delete(registration.contract.id);
      const unsafeLeave = cause?.code === "PRESENCE_CREDENTIAL_DENIED" || cause?.code === "LIVE_RESOURCE_CAPABILITY_DENIED" || cause?.code === "PRESENCE_ROUTE_DENIED";
      if (!unsafeLeave && memberId !== null) {
        const stale = { ...registration, memberId, closed: true };
        void writePresence(stale, "leave", null, true).catch(() => {});
      }
      reconcile();
      return;
    }
    registration.generation += 1;
    registration.joinStarted = false;
    registration.memberId = null;
    registration.joinedVersion = 0;
    if (cause?.code === "PRESENCE_MEMBERSHIP_EXPIRED") {
      registration.joinNonce = presenceCredential("join nonce");
      registration.token = presenceCredential("credential");
    }
    if (registration.heartbeat !== null) presenceClearInterval?.(registration.heartbeat);
    if (registration.retryTimer !== null) presenceClearTimeout?.(registration.retryTimer);
    registration.heartbeat = null;
    registration.retryTimer = null;
    registration.heartbeatWriting = false;
    reconcile();
  }

  async function drainPresenceUpdates(registration) {
    if (registration.updating || registration.closed || registration.memberId === null || registration.retryTimer !== null) return;
    registration.updating = true;
    const generation = registration.generation;
    try {
      while (!registration.closed && registration.generation === generation && registration.joinedVersion < registration.recordVersion) {
        const sentVersion = registration.recordVersion;
        const sentRecord = registration.record;
        await writePresence(registration, "update", sentRecord);
        if (registration.generation !== generation) return;
        registration.joinedVersion = Math.max(registration.joinedVersion, sentVersion);
      }
    } catch (cause) {
      if (registration.generation === generation) recoverPresence(registration, cause);
    } finally {
      registration.updating = false;
      if (!registration.closed && registration.memberId !== null && registration.retryTimer === null && registration.joinedVersion < registration.recordVersion) {
        void drainPresenceUpdates(registration);
      }
    }
  }

  async function joinPresence(registration) {
    const generation = registration.generation;
    const sentVersion = registration.recordVersion;
    const sentRecord = registration.record;
    try {
      const result = await writePresence(registration, "join", sentRecord);
      if (typeof result?.memberId !== "string" || !/^[A-Za-z0-9_-]{16,128}$/.test(result.memberId)) {
        throw liveResourceFailure("PRESENCE_JOIN_CREDENTIALS_INVALID", "Presence join omitted its compiler-owned member identity");
      }
      if (registration.generation !== generation || registration.closed) {
        const stale = { ...registration, memberId: result.memberId, token: registration.token, closed: true };
        void writePresence(stale, "leave", null, true).catch(() => {});
        return;
      }
      registration.memberId = result.memberId;
      registration.joinedVersion = sentVersion;
      registration.heartbeat = presenceSetInterval?.(() => {
        heartbeatPresence(registration);
      }, registration.contract.heartbeatMilliseconds);
      registration.heartbeat?.unref?.();
      void drainPresenceUpdates(registration);
    } catch (cause) {
      if (registration.generation === generation) recoverPresence(registration, cause);
    }
  }

  function heartbeatPresence(registration) {
    if (registration.closed || registration.memberId === null || registration.heartbeatWriting || registration.retryTimer !== null) return;
    const heartbeatGeneration = registration.generation;
    const heartbeatMember = registration.memberId;
    const heartbeatToken = registration.token;
    registration.heartbeatWriting = true;
    void writePresence(registration, "heartbeat")
      .then(() => {
        if (registration.generation === heartbeatGeneration && registration.joinedVersion < registration.recordVersion) void drainPresenceUpdates(registration);
      })
      .catch((cause) => {
        if (registration.generation === heartbeatGeneration && registration.memberId === heartbeatMember && registration.token === heartbeatToken) recoverPresence(registration, cause);
      })
      .finally(() => { if (registration.generation === heartbeatGeneration) registration.heartbeatWriting = false; });
  }

  function dispatchPresence(presenceId, event, routeId, routePath) {
    for (const registration of [...(presenceRegistrations.get(presenceId) ?? [])]) {
      if (registration.routeId !== routeId || registration.routePath !== routePath) continue;
      deliverPresence(registration, event);
    }
  }

  function retryDelay(signal) {
    return new Promise((resolve) => {
      if (signal.aborted || retryMilliseconds === 0) { resolve(); return; }
      const timer = setTimeout(finish, retryMilliseconds);
      function finish() { signal.removeEventListener("abort", cancel); resolve(); }
      function cancel() { clearTimeout(timer); signal.removeEventListener("abort", cancel); resolve(); }
      signal.addEventListener("abort", cancel, { once: true });
      timer?.unref?.();
    });
  }

  async function connectSse(ids, presenceIds, key, routeId, routePath, signal) {
    const url = new URL(`${basePath}/_noxid/live`, globalThis.location?.href ?? "http://noxid.invalid/");
    for (const id of ids) url.searchParams.append("resource", id);
    for (const id of presenceIds) url.searchParams.append("presence", id);
    const headers = { accept: "text/event-stream", "x-noxid-route-id": routeId, "x-noxid-route-path": routePath };
    const resume = cursors.get(key);
    if (resume !== undefined) headers["last-event-id"] = resume;
    const response = await fetcher(url, { method: "GET", headers, signal });
    if (!response?.ok) {
      let details = null;
      try { details = await response?.json(); } catch {}
      const failure = liveResourceFailure(details?.error?.code ?? "LIVE_RESOURCE_RESPONSE_INVALID", details?.error?.message ?? "Live resource endpoint rejected the connection");
      failure.retryable = response?.status === 429 || response?.status >= 500;
      failure.permanent = !failure.retryable;
      throw failure;
    }
    if (response.headers?.get("content-type")?.split(";", 1)[0].trim().toLowerCase() !== "text/event-stream" || !response.body) {
      const failure = liveResourceFailure("LIVE_RESOURCE_RESPONSE_INVALID", "Live resource endpoint did not return a readable SSE response");
      failure.permanent = true;
      throw failure;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8", { fatal: true });
    const encoder = new TextEncoder();
    const subscriptionSet = new Set(ids);
    const presenceSubscriptionSet = new Set(presenceIds);
    let pendingText = "";
    let frameLines = [];
    let frameBytes = 0;
    try {
      while (!signal.aborted) {
        const chunk = await reader.read();
        try { pendingText += decoder.decode(chunk.value ?? new Uint8Array(), { stream: !chunk.done }); }
        catch (cause) { throw liveResourceFailure("LIVE_RESOURCE_EVENT_UTF8_INVALID", "Live connection emitted invalid UTF-8", cause); }
        const lines = pendingText.split("\n");
        pendingText = lines.pop() ?? "";
        for (const rawLine of lines) {
          const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
          if (line !== "") {
            frameLines.push(line);
            frameBytes += encoder.encode(`${line}\n`).byteLength;
            if (frameBytes > LIVE_RESOURCE_FRAME_LIMIT_BYTES) throw liveResourceFailure("LIVE_RESOURCE_FRAME_TOO_LARGE", "Live connection exceeded the bounded SSE framing allowance");
            continue;
          }
          const frame = decodeLiveResourceFrame(frameLines, manifest, subscriptionSet, presenceManifest, presenceSubscriptionSet);
          frameLines = [];
          frameBytes = 0;
          if (frame?.reset === true) {
            cursors.delete(key);
            invalidateAll(ids, routeId, routePath);
            return;
          }
          if (frame?.sync === true) {
            invalidateAll(ids, routeId, routePath);
            continue;
          }
          if (frame?.presence) {
            dispatchPresence(frame.presence, frame.event, routeId, routePath);
            continue;
          }
          if (frame) {
            // Cursor ownership advances only after the frame has passed the
            // compiled manifest and exact current-subscription validation.
            rememberCursor(key, frame.id);
            enqueue(frame.semanticId, routeId, routePath);
          }
        }
        if (frameBytes + encoder.encode(pendingText).byteLength > LIVE_RESOURCE_FRAME_LIMIT_BYTES) throw liveResourceFailure("LIVE_RESOURCE_FRAME_TOO_LARGE", "Live connection exceeded the bounded SSE framing allowance");
        if (chunk.done) break;
      }
      if (!signal.aborted && pendingText !== "") frameLines.push(pendingText);
      if (!signal.aborted && frameLines.length > 0) {
        const frame = decodeLiveResourceFrame(frameLines, manifest, subscriptionSet, presenceManifest, presenceSubscriptionSet);
        if (frame?.reset === true) {
          cursors.delete(key);
          invalidateAll(ids, routeId, routePath);
        } else if (frame?.sync === true) {
          invalidateAll(ids, routeId, routePath);
        } else if (frame?.presence) {
          dispatchPresence(frame.presence, frame.event, routeId, routePath);
        } else if (frame) {
          rememberCursor(key, frame.id);
          enqueue(frame.semanticId, routeId, routePath);
        }
      }
    } finally {
      await reader.cancel(signal.reason).catch(() => {});
    }
  }

  function connectWebSocket(presenceIds, key, routeId, routePath, signal) {
    if (typeof WebSocketConstructor !== "function") {
      return Promise.reject(liveResourceFailure("WEBSOCKET_UNAVAILABLE", "This browser does not expose a WebSocket transport"));
    }
    const url = new URL(`${basePath}/_noxid/live`, globalThis.location?.href ?? "http://noxid.invalid/");
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    for (const id of presenceIds) url.searchParams.append("presence", id);
    url.searchParams.set("route", routeId);
    url.searchParams.set("routePath", routePath);
    const resume = cursors.get(key);
    if (resume !== undefined) url.searchParams.set("cursor", resume);
    const subscriptionSet = new Set();
    const presenceSubscriptionSet = new Set(presenceIds);
    return new Promise((resolve, reject) => {
      let socket;
      try { socket = new WebSocketConstructor(url.href); }
      catch (cause) { reject(liveResourceFailure("WEBSOCKET_UPGRADE_FAILED", "WebSocket upgrade could not be started", cause)); return; }
      const writes = new Map();
      let settled = false;
      let opened = false;
      const fail = (cause) => {
        if (settled) return;
        settled = true;
        for (const pendingWrite of writes.values()) pendingWrite.reject(cause);
        writes.clear();
        for (const id of presenceIds) websocketPresenceWrites.delete(presenceWriteKey(id, routeId, routePath));
        signal.removeEventListener("abort", abort);
        reject(cause);
      };
      const finish = () => {
        if (settled) return;
        settled = true;
        for (const pendingWrite of writes.values()) pendingWrite.reject(liveResourceFailure("WEBSOCKET_CLOSED", "WebSocket presence transport closed"));
        writes.clear();
        for (const id of presenceIds) websocketPresenceWrites.delete(presenceWriteKey(id, routeId, routePath));
        signal.removeEventListener("abort", abort);
        resolve();
      };
      const abort = () => { try { socket.close(1000, "subscription changed"); } catch {} finish(); };
      signal.addEventListener("abort", abort, { once: true });
      socket.addEventListener("open", () => {
        opened = true;
        const write = (frame) => new Promise((resolveWrite, rejectWrite) => {
          const encoded = JSON.stringify(frame);
          if (new TextEncoder().encode(encoded).byteLength > LIVE_RESOURCE_FRAME_LIMIT_BYTES) {
            rejectWrite(liveResourceFailure("WEBSOCKET_FRAME_TOO_LARGE", "WebSocket frames are bounded to 64 KiB"));
            return;
          }
          if (writes.size >= 256) {
            rejectWrite(liveResourceFailure("WEBSOCKET_PENDING_FRAME_LIMIT", "WebSocket connection already has 256 pending frames"));
            return;
          }
          writes.set(frame.requestId, { resolve: resolveWrite, reject: rejectWrite });
          try { socket.send(encoded); }
          catch (cause) { writes.delete(frame.requestId); rejectWrite(cause); }
        });
        for (const id of presenceIds) websocketPresenceWrites.set(presenceWriteKey(id, routeId, routePath), write);
      });
      socket.addEventListener("message", (message) => {
        if (typeof message.data !== "string" || new TextEncoder().encode(message.data).byteLength > LIVE_RESOURCE_FRAME_LIMIT_BYTES) {
          fail(liveResourceFailure("WEBSOCKET_FRAME_TOO_LARGE", "WebSocket server frame exceeded 64 KiB"));
          try { socket.close(1009, "frame too large"); } catch {}
          return;
        }
        let frame;
        try { frame = JSON.parse(message.data); }
        catch (cause) { fail(liveResourceFailure("WEBSOCKET_FRAME_INVALID", "WebSocket server frame was not typed JSON", cause)); return; }
        if (frame?.type === "presence.result") {
          const pendingWrite = typeof frame.requestId === "string" ? writes.get(frame.requestId) : null;
          if (!pendingWrite) { fail(liveResourceFailure("WEBSOCKET_FRAME_INVALID", "WebSocket presence result did not match a pending compiler-owned write")); return; }
          writes.delete(frame.requestId);
          const { type: _type, requestId: _requestId, ...result } = frame;
          pendingWrite.resolve(result);
          return;
        }
        if (frame?.type !== "event" || typeof frame.event !== "string" || (frame.id !== null && typeof frame.id !== "string")) {
          fail(liveResourceFailure("WEBSOCKET_FRAME_INVALID", "WebSocket server frame did not match the typed realtime event envelope"));
          return;
        }
        let decoded;
        try {
          const lines = [`event: ${frame.event}`];
          if (frame.id !== null) lines.push(`id: ${frame.id}`);
          lines.push(`data: ${JSON.stringify(frame.data)}`);
          decoded = decodeLiveResourceFrame(lines, manifest, subscriptionSet, presenceManifest, presenceSubscriptionSet);
        } catch (cause) { fail(cause); return; }
        if (decoded?.reset === true) {
          cursors.delete(key);
          return;
        }
        if (decoded?.presence) dispatchPresence(decoded.presence, decoded.event, routeId, routePath);
      });
      socket.addEventListener("error", () => fail(liveResourceFailure(opened ? "WEBSOCKET_CLOSED" : "WEBSOCKET_UPGRADE_FAILED", opened ? "WebSocket realtime transport failed" : "WebSocket upgrade failed")));
      socket.addEventListener("close", () => {
        if (signal.aborted) finish();
        else fail(liveResourceFailure(opened ? "WEBSOCKET_CLOSED" : "WEBSOCKET_UPGRADE_FAILED", opened ? "WebSocket realtime transport closed" : "WebSocket upgrade was refused"));
      });
    });
  }

  async function connectWebSocketWithFallback(presenceIds, key, routeId, routePath, signal) {
    try {
      await connectWebSocket(presenceIds, key, routeId, routePath, signal);
    } catch (cause) {
      if (signal.aborted) return;
      if (cause?.code !== "WEBSOCKET_UNAVAILABLE" && cause?.code !== "WEBSOCKET_UPGRADE_FAILED") throw cause;
      emitDevtools("realtime.transport.fallback", {
        semanticId: presenceIds.join(","),
        payload: { from: "websocket", to: "sse", code: cause?.code ?? "WEBSOCKET_UPGRADE_FAILED", count: presenceIds.length },
      });
      await connectSse([], presenceIds, key, routeId, routePath, signal);
    }
  }

  async function supervise() {
    while (!stopped) {
      const configuration = activeConfiguration();
      if (configuration === null) return;
      const { ids, presenceIds, websocketPresenceIds, key, websocketKey, routeId, routePath } = configuration;
      const version = desiredVersion;
      const controller = new AbortController();
      cycleController = controller;
      try {
        const connections = [];
        if (ids.length > 0 || presenceIds.length > 0) connections.push(connectSse(ids, presenceIds, key, routeId, routePath, controller.signal));
        if (websocketPresenceIds.length > 0) connections.push(connectWebSocketWithFallback(websocketPresenceIds, websocketKey, routeId, routePath, controller.signal));
        await Promise.all(connections);
      } catch (error) {
        if (!controller.signal.aborted) {
          cursors.delete(key);
          if (error?.permanent === true) {
            for (const id of [...presenceIds, ...websocketPresenceIds]) {
              for (const registration of [...(presenceRegistrations.get(id) ?? [])]) {
                if (registration.routeId === routeId && registration.routePath === routePath) recoverPresence(registration, error);
              }
            }
            for (const id of ids) {
              const entries = registrations.get(id);
              for (const registration of [...(entries ?? [])]) {
                if (registration.routeId === routeId && registration.routePath === routePath) entries.delete(registration);
              }
              if (entries?.size === 0) registrations.delete(id);
            }
          } else {
            invalidateAll(ids, routeId, routePath);
          }
        }
      } finally {
        if (cycleController === controller) cycleController = null;
      }
      if (stopped) return;
      if (desiredVersion !== version) continue;
      cycleController = controller;
      await retryDelay(controller.signal);
      if (cycleController === controller) cycleController = null;
    }
  }

  function reconcile() {
    desiredVersion += 1;
    cycleController?.abort("Live resource subscription set changed");
    if (reconciliationScheduled || stopped) return;
    reconciliationScheduled = true;
    queueMicrotask(() => {
      reconciliationScheduled = false;
      if (supervisor !== null || stopped) return;
      supervisor = supervise().finally(() => {
        supervisor = null;
        if (!stopped && (registrations.size > 0 || presenceRegistrations.size > 0)) reconcile();
      });
    });
  }

  return Object.freeze({
    register(definition, queryClient = definition?.queryClient, routeId = undefined, routePath = undefined) {
      if (stopped) throw liveResourceFailure("LIVE_RESOURCE_CONTROLLER_STOPPED", "Live resource controller has stopped");
      const id = definition?.id;
      if (definition?.live !== true || !manifest.has(id) || !queryClient || typeof queryClient.invalidateResource !== "function") {
        throw liveResourceFailure("LIVE_RESOURCE_REGISTRATION_INVALID", "Live acquisition must match the compiler-owned resource manifest and query client");
      }
      if (typeof routeId !== "string" || !routeId.startsWith("route:") || routeId.length > 512) {
        throw liveResourceFailure("LIVE_RESOURCE_ROUTE_REQUIRED", "Live acquisition requires its compiler-selected route identity");
      }
      if (typeof routePath !== "string" || !routePath.startsWith("/") || routePath.length > 2048 || routePath.includes("?") || routePath.includes(String.fromCharCode(35))) {
        throw liveResourceFailure("LIVE_RESOURCE_ROUTE_PATH_REQUIRED", "Live acquisition requires its compiler-selected application route path");
      }
      const registration = Object.freeze({ definition, queryClient, routeId, routePath, ordinal: ++nextRegistrationOrdinal });
      let entries = registrations.get(id);
      if (!entries) { entries = new Set(); registrations.set(id, entries); }
      entries.add(registration);
      reconcile();
      let active = true;
      return () => {
        if (!active) return;
        active = false;
        entries.delete(registration);
        if (entries.size === 0) {
          registrations.delete(id);
          for (const key of [...pending.keys()]) {
            if (pending.get(key)?.semanticId === id) pending.delete(key);
          }
        }
        reconcile();
      };
    },
    registerPresence(contract, record, routeId = undefined, routePath = undefined) {
      if (stopped) throw liveResourceFailure("LIVE_RESOURCE_CONTROLLER_STOPPED", "Live controller has stopped");
      if (!contract || typeof contract !== "object" || !presenceManifest.has(contract.id)
        || typeof contract.stream !== "string" || typeof contract.validateRecord !== "function"
        || !Number.isInteger(contract.ttlMilliseconds) || contract.ttlMilliseconds < 5000 || contract.ttlMilliseconds > 300000
        || !Number.isInteger(contract.heartbeatMilliseconds) || contract.heartbeatMilliseconds < 1000 || contract.heartbeatMilliseconds >= contract.ttlMilliseconds
        || (contract.transport !== undefined && contract.transport !== "sse" && contract.transport !== "websocket")
        || (contract.transport === "websocket") !== websocketPresenceManifest.has(contract.id)
        || (contract.idleTimeoutMilliseconds !== undefined && (!Number.isInteger(contract.idleTimeoutMilliseconds) || contract.idleTimeoutMilliseconds <= contract.heartbeatMilliseconds))) {
        throw liveResourceFailure("PRESENCE_REGISTRATION_INVALID", "Presence acquisition must match the compiler-owned typed manifest");
      }
      if (typeof routeId !== "string" || !routeId.startsWith("route:") || routeId.length > 512) {
        throw liveResourceFailure("LIVE_RESOURCE_ROUTE_REQUIRED", "Presence acquisition requires its compiler-selected route identity");
      }
      if (typeof routePath !== "string" || !routePath.startsWith("/") || routePath.length > 2048 || routePath.includes("?") || routePath.includes(String.fromCharCode(35))) {
        throw liveResourceFailure("LIVE_RESOURCE_ROUTE_PATH_REQUIRED", "Presence acquisition requires its compiler-selected application route path");
      }
      const registration = {
        contract,
        routeId,
        routePath,
        ordinal: ++nextRegistrationOrdinal,
        record: contract.validateRecord(record),
        recordVersion: 1,
        joinedVersion: 0,
        generation: 1,
        updating: false,
        memberId: null,
        token: presenceCredential("credential"),
        joinNonce: presenceCredential("join nonce"),
        joinStarted: false,
        heartbeat: null,
        heartbeatWriting: false,
        retryTimer: null,
        queue: [],
        waiters: [],
        awaitingSnapshot: false,
        failure: null,
        closed: false,
        fail(cause) {
          if (registration.closed || registration.failure) return;
          registration.failure = cause instanceof Error ? cause : liveResourceFailure("PRESENCE_STREAM_FAILED", "Presence stream failed", cause);
          for (const waiter of registration.waiters.splice(0)) waiter.reject(registration.failure);
        },
        update(next) {
          if (registration.closed) return;
          const trusted = contract.validateRecord(next);
          if (stableAcquisitionKey(trusted) === stableAcquisitionKey(registration.record)) return;
          registration.record = trusted;
          registration.recordVersion += 1;
          void drainPresenceUpdates(registration);
        },
        close(sendLeave = true) {
          if (registration.closed) return;
          registration.closed = true;
          if (registration.heartbeat !== null) presenceClearInterval?.(registration.heartbeat);
          if (registration.retryTimer !== null) presenceClearTimeout?.(registration.retryTimer);
          registration.heartbeat = null;
          registration.retryTimer = null;
          registration.generation += 1;
          if (sendLeave && registration.memberId !== null) void writePresence(registration, "leave", null, true).catch(() => {});
          for (const waiter of registration.waiters.splice(0)) waiter.resolve({ done: true, value: undefined });
          registration.queue = [];
          const entries = presenceRegistrations.get(contract.id);
          entries?.delete(registration);
          if (entries?.size === 0) presenceRegistrations.delete(contract.id);
          reconcile();
        },
      };
      let entries = presenceRegistrations.get(contract.id);
      if (!entries) { entries = new Set(); presenceRegistrations.set(contract.id, entries); }
      entries.add(registration);
      registration.source = presenceSource(registration);
      reconcile();
      return Object.freeze({ source: registration.source, update: registration.update, close: registration.close });
    },
    stop() {
      if (stopped) return;
      stopped = true;
      desiredVersion += 1;
      cycleController?.abort("Live resource controller stopped");
      cycleController = null;
      registrations.clear();
      for (const entries of [...presenceRegistrations.values()]) {
        for (const registration of [...entries]) registration.close(false);
      }
      presenceRegistrations.clear();
      pending.clear();
      cursors.clear();
      websocketPresenceWrites.clear();
    },
  });
}

export function installLiveResourceController(controller) {
  if (!controller || typeof controller.register !== "function" || typeof controller.registerPresence !== "function" || typeof controller.stop !== "function") {
    throw new TypeError("Only a compiler-owned live resource controller can be installed");
  }
  if (__noxidInstalledLiveResourceController !== null && __noxidInstalledLiveResourceController !== controller) {
    throw liveResourceFailure("LIVE_RESOURCE_CONTROLLER_DUPLICATE", "A browser project may install exactly one live resource controller");
  }
  __noxidInstalledLiveResourceController = controller;
  let installed = true;
  return () => {
    if (!installed) return;
    installed = false;
    if (__noxidInstalledLiveResourceController === controller) __noxidInstalledLiveResourceController = null;
  };
}

export function acquirePresence(
  definition,
  contract,
  readRecord,
  owner = currentOwner,
  sources = null,
  options = {},
  semanticId = "presence",
) {
  if (!owner || owner.disposed) throw liveResourceFailure("PRESENCE_OWNER_REQUIRED", "Presence acquisition requires a live Owner");
  if (typeof readRecord !== "function") throw liveResourceFailure("PRESENCE_RECORD_READER_REQUIRED", "Presence requires compiler-generated typed field readers");
  const liveController = options.liveResourceController ?? __noxidInstalledLiveResourceController;
  if (!liveController || typeof liveController.registerPresence !== "function") {
    throw liveResourceFailure("PRESENCE_CONTROLLER_REQUIRED", "Presence requires the project-global live connection controller");
  }
  const routeId = options.route?.id;
  const routePath = options.route?.path;
  const initialRecord = readRecord();
  const registration = liveController.registerPresence(contract, initialRecord, routeId, routePath);
  const handle = acquireStream(
    definition,
    () => initialRecord,
    owner,
    [],
    { ...options, connect: () => registration.source },
    semanticId,
  );
  effect(() => registration.update(readRecord()), owner, sources);
  onCleanup(owner, () => registration.close(true));
  return handle;
}


export function invalidateCompiledResources(queryClient, definitions, runtimeOptions = {}, actionId = null) {
  const targets = [...new Map(definitions.map((definition) => [definition.id, definition])).values()];
  if (runtimeOptions?.scenarioHarnessToken === scenarioHarnessToken) {
    for (const definition of targets) {
      for (const handle of runtimeOptions.scenarioResourceHandles.get(definition.id) ?? []) {
        const lifecycle = handle.state.get();
        if (lifecycle.tag === "Ready" || lifecycle.tag === "Refreshing") {
          handle.state.set(Object.freeze({ ...lifecycle, tag: "Refreshing" }));
        }
      }
    }
  }
  for (const definition of targets) {
    try {
      Promise.resolve(queryClient.invalidateResource(definition)).catch((error) => {
        emitDevtools("resource.invalidation.refresh.failed", {
          semanticId: actionId,
          payload: { resource: definition.id, code: error?.code ?? null },
        });
      });
    } catch (error) {
      emitDevtools("resource.invalidation.refresh.failed", {
        semanticId: actionId,
        payload: { resource: definition.id, code: error?.code ?? null },
      });
    }
  }
  flush();
}


export class StreamExecutionError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = "StreamExecutionError";
    this.code = code;
    this.stream = details.stream ?? null;
    this.event = details.event ?? null;
    this.parameter = details.parameter ?? null;
    this.expected = details.expected ?? null;
    if (details.cause !== undefined) this.cause = details.cause;
  }

  toJSON() {
    return {
      code: this.code,
      stream: this.stream,
      event: this.event,
      parameter: this.parameter,
      expected: this.expected,
    };
  }
}

function streamFailure(code, message, config, details = {}) {
  return new StreamExecutionError(code, message, { stream: config.id, ...details });
}

function endpointStreamFailure(code, message, config, details = {}) {
  return new StreamExecutionError(code, message, {
    stream: config.stream,
    ...details,
  });
}

const ENDPOINT_STREAM_EVENT_LIMIT_BYTES = 262_144;
const ENDPOINT_STREAM_FRAME_LIMIT_BYTES = 276_480;

function endpointStreamEventId(value) {
  if (typeof value !== "string" || value.length > 256) return null;
  const match = /^([A-Za-z0-9_-]{16,128}):([1-9][0-9]{0,15})$/.exec(value);
  if (match === null) return null;
  const sequence = Number(match[2]);
  return Number.isSafeInteger(sequence) ? value : null;
}

function endpointStreamUrl(config, parameters) {
  let path = config.path;
  for (const name of config.params) {
    if (!Object.prototype.hasOwnProperty.call(parameters, name)) {
      throw endpointStreamFailure("STREAM_ENDPOINT_PARAMETER_MISSING", `${config.endpoint} is missing path parameter ${name}`, config, { parameter: name });
    }
    path = path.replace(`[${name}]`, encodeURIComponent(String(parameters[name])));
  }
  const prefix = config.basePath === "/" ? "" : config.basePath;
  const url = new URL(`${prefix}${path}`, globalThis.location?.href ?? "http://noxid.invalid/");
  for (const field of config.query) {
    const value = parameters[field.name];
    if (value === null || value === undefined) continue;
    const encoded = field.type.includes("Array<") ? JSON.stringify(value) : String(value);
    url.searchParams.set(field.name, encoded);
  }
  return url;
}

function decodeEndpointStreamFrame(lines, config) {
  let event = "message";
  let id = null;
  const data = [];
  for (const line of lines) {
    if (line === "" || line.startsWith(":")) continue;
    const separator = line.indexOf(":");
    const field = separator < 0 ? line : line.slice(0, separator);
    let value = separator < 0 ? "" : line.slice(separator + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
    else if (field === "id" && !value.includes("\0")) id = value;
  }
  if (data.length === 0) return null;
  const encodedData = data.join("\n");
  if (new TextEncoder().encode(encodedData).byteLength > ENDPOINT_STREAM_EVENT_LIMIT_BYTES) {
    throw endpointStreamFailure("STREAM_ENDPOINT_EVENT_TOO_LARGE", `${config.endpoint} exceeded the 256KiB SSE event limit`, config);
  }
  let payload;
  try {
    payload = JSON.parse(encodedData);
  } catch (cause) {
    throw endpointStreamFailure("STREAM_ENDPOINT_EVENT_JSON_INVALID", `${config.endpoint} emitted invalid SSE JSON`, config, { cause });
  }
  if (event === "noxid-error") {
    const remote = payload?.error;
    const code = typeof remote?.code === "string" ? remote.code : "STREAM_ENDPOINT_REMOTE_FAILED";
    const message = typeof remote?.message === "string" ? remote.message : `${config.endpoint} terminated with a structured stream error`;
    throw endpointStreamFailure(code, message, config, { cause: payload });
  }
  if (event !== "message") {
    throw endpointStreamFailure("STREAM_ENDPOINT_EVENT_UNKNOWN", `${config.endpoint} emitted unsupported SSE event ${event}`, config, { event });
  }
  if (id === null || id.length === 0) {
    throw endpointStreamFailure("STREAM_ENDPOINT_EVENT_ID_REQUIRED", `${config.endpoint} emitted a message without an SSE id`, config);
  }
  if (endpointStreamEventId(id) === null) {
    throw endpointStreamFailure("STREAM_ENDPOINT_EVENT_ID_INVALID", `${config.endpoint} emitted an SSE id outside the compiler-owned token:sequence grammar`, config);
  }
  return Object.freeze({ id, value: payload });
}

export function createEndpointStreamConnector(input) {
  if (!input || typeof input !== "object" || typeof input.stream !== "string" || typeof input.endpoint !== "string" || typeof input.path !== "string") {
    throw new TypeError("Endpoint stream connector requires compiler-owned stream, endpoint, and path identities");
  }
  const params = [...(input.params ?? [])];
  const query = [...(input.query ?? [])];
  if (params.some((name) => typeof name !== "string" || name.length === 0)
    || query.some((field) => !field || typeof field.name !== "string" || field.name.length === 0 || typeof field.type !== "string" || field.type.length === 0)) {
    throw new TypeError("Endpoint stream connector parameters require compiler-owned names and query types");
  }
  const inputNames = [...params, ...query.map((field) => field.name)];
  if (new Set(inputNames).size !== inputNames.length) {
    throw new TypeError("Endpoint stream connector parameter names must be unique");
  }
  const config = Object.freeze({
    stream: input.stream,
    endpoint: input.endpoint,
    path: input.path,
    basePath: input.basePath === "/" ? "/" : String(input.basePath ?? "").replace(/\/$/, ""),
    transport: input.transport ?? "sse",
    heartbeatMilliseconds: input.heartbeatMilliseconds ?? 15_000,
    idleTimeoutMilliseconds: input.idleTimeoutMilliseconds ?? 45_000,
    params: Object.freeze(params),
    query: Object.freeze(query.map((field) => Object.freeze({ name: field.name, type: field.type }))),
  });
  if ((config.transport !== "sse" && config.transport !== "websocket")
    || !Number.isInteger(config.heartbeatMilliseconds) || config.heartbeatMilliseconds < 1000
    || !Number.isInteger(config.idleTimeoutMilliseconds) || config.idleTimeoutMilliseconds <= config.heartbeatMilliseconds) {
    throw new TypeError("Endpoint stream connector requires a compiler-owned realtime transport contract");
  }
  const WebSocketConstructor = input.WebSocket ?? globalThis.WebSocket;
  const resumeByParameters = new Map();
  const rememberResumeToken = (key, token) => {
    resumeByParameters.delete(key);
    resumeByParameters.set(key, token);
    if (resumeByParameters.size > 128) {
      resumeByParameters.delete(resumeByParameters.keys().next().value);
    }
  };
  async function* connectEndpointSse(parameters, { signal, resumeToken = null } = {}) {
    const key = stableAcquisitionKey(parameters);
    const lastEventId = resumeToken ?? resumeByParameters.get(key) ?? null;
    if (lastEventId !== null && endpointStreamEventId(lastEventId) === null) {
      throw endpointStreamFailure("STREAM_ENDPOINT_RESUME_TOKEN_INVALID", `${config.endpoint} received an invalid resume token; only IDs previously emitted by the compiler-owned SSE endpoint may be resumed`, config);
    }
    const headers = { accept: "text/event-stream" };
    if (lastEventId !== null) headers["last-event-id"] = String(lastEventId);
    const response = await fetch(endpointStreamUrl(config, parameters), { method: "GET", headers, signal });
    if (!response.ok) {
      let payload = null;
      try { payload = await response.json(); } catch {}
      const remote = payload?.error;
      throw endpointStreamFailure(
        typeof remote?.code === "string" ? remote.code : "STREAM_ENDPOINT_HTTP_FAILED",
        typeof remote?.message === "string" ? remote.message : `${config.endpoint} returned HTTP ${response.status}`,
        config,
        { cause: payload },
      );
    }
    const contentType = response.headers.get("content-type")?.split(";", 1)[0].trim().toLowerCase();
    if (contentType !== "text/event-stream" || !response.body) {
      throw endpointStreamFailure("STREAM_ENDPOINT_RESPONSE_INVALID", `${config.endpoint} must return text/event-stream with a readable body`, config);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8", { fatal: true });
    const encoder = new TextEncoder();
    let pending = "";
    let frameLines = [];
    let frameBytes = 0;
    try {
      while (true) {
        const chunk = await reader.read();
        try {
          pending += decoder.decode(chunk.value ?? new Uint8Array(), { stream: !chunk.done });
        } catch (cause) {
          throw endpointStreamFailure("STREAM_ENDPOINT_EVENT_UTF8_INVALID", `${config.endpoint} emitted invalid UTF-8 in its SSE stream`, config, { cause });
        }
        // Split on LF so a CRLF pair divided across chunks cannot synthesize
        // a false blank line and dispatch a partial frame.
        const lines = pending.split("\n");
        pending = lines.pop() ?? "";
        for (const rawLine of lines) {
          const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
          if (line !== "") {
            frameLines.push(line);
            frameBytes += encoder.encode(`${line}\n`).byteLength;
            if (frameBytes > ENDPOINT_STREAM_FRAME_LIMIT_BYTES) {
              throw endpointStreamFailure("STREAM_ENDPOINT_FRAME_TOO_LARGE", `${config.endpoint} exceeded the bounded SSE framing allowance`, config);
            }
            continue;
          }
          const frame = decodeEndpointStreamFrame(frameLines, config);
          frameLines = [];
          frameBytes = 0;
          if (!frame) continue;
          yield Object.freeze({ tag: "Ready", value: frame.value });
          // An async generator resumes here only after acquireStream validates
          // the yielded Ready payload. Never advance a resume cursor for an
          // event rejected by the ordinary typed-stream boundary.
          rememberResumeToken(key, frame.id);
        }
        if (frameBytes + encoder.encode(pending).byteLength > ENDPOINT_STREAM_FRAME_LIMIT_BYTES) {
          throw endpointStreamFailure("STREAM_ENDPOINT_FRAME_TOO_LARGE", `${config.endpoint} exceeded the bounded SSE framing allowance`, config);
        }
        if (chunk.done) break;
      }
      if (pending !== "") frameLines.push(pending);
      const trailing = decodeEndpointStreamFrame(frameLines, config);
      if (trailing) {
        yield Object.freeze({ tag: "Ready", value: trailing.value });
        rememberResumeToken(key, trailing.id);
      }
    } finally {
      await reader.cancel(signal?.reason).catch(() => {});
    }
  }

  async function* connectEndpointWebSocket(parameters, { signal, resumeToken = null } = {}) {
    if (typeof WebSocketConstructor !== "function") {
      throw endpointStreamFailure("WEBSOCKET_UNAVAILABLE", `${config.endpoint} cannot upgrade because this browser has no WebSocket implementation`, config);
    }
    const key = stableAcquisitionKey(parameters);
    const lastEventId = resumeToken ?? resumeByParameters.get(key) ?? null;
    if (lastEventId !== null && endpointStreamEventId(lastEventId) === null) {
      throw endpointStreamFailure("STREAM_ENDPOINT_RESUME_TOKEN_INVALID", `${config.endpoint} received an invalid resume token`, config);
    }
    const url = endpointStreamUrl(config, parameters);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    if (lastEventId !== null) url.searchParams.set("cursor", String(lastEventId));
    let socket;
    try { socket = new WebSocketConstructor(url.href); }
    catch (cause) { throw endpointStreamFailure("WEBSOCKET_UPGRADE_FAILED", `${config.endpoint} WebSocket upgrade could not be started`, config, { cause }); }
    const queued = [];
    let waiter = null;
    let terminal = null;
    let opened = false;
    const push = (value) => {
      if (waiter) { const current = waiter; waiter = null; current.resolve(value); }
      else queued.push(value);
    };
    const fail = (cause) => {
      if (terminal) return;
      terminal = cause;
      if (waiter) { const current = waiter; waiter = null; current.reject(cause); }
    };
    const next = () => {
      if (queued.length > 0) return Promise.resolve(queued.shift());
      if (terminal) return Promise.reject(terminal);
      return new Promise((resolve, reject) => { waiter = { resolve, reject }; });
    };
    const abort = () => {
      terminal = endpointStreamFailure("STREAM_ABORTED", `${config.endpoint} stream was aborted`, config);
      try { socket.close(1000, "stream aborted"); } catch {}
      if (waiter) { const current = waiter; waiter = null; current.resolve(null); }
    };
    signal?.addEventListener("abort", abort, { once: true });
    socket.addEventListener("open", () => { opened = true; });
    socket.addEventListener("message", (message) => {
      if (typeof message.data !== "string" || new TextEncoder().encode(message.data).byteLength > ENDPOINT_STREAM_FRAME_LIMIT_BYTES) {
        fail(endpointStreamFailure("WEBSOCKET_FRAME_TOO_LARGE", `${config.endpoint} WebSocket frame exceeded 64 KiB`, config));
        return;
      }
      let envelope;
      try { envelope = JSON.parse(message.data); }
      catch (cause) { fail(endpointStreamFailure("WEBSOCKET_FRAME_INVALID", `${config.endpoint} emitted invalid typed WebSocket JSON`, config, { cause })); return; }
      if (envelope?.type !== "event" || typeof envelope.event !== "string" || (envelope.id !== null && typeof envelope.id !== "string")) {
        fail(endpointStreamFailure("WEBSOCKET_FRAME_INVALID", `${config.endpoint} emitted an invalid typed WebSocket event envelope`, config));
        return;
      }
      try {
        const lines = [`event: ${envelope.event}`];
        if (envelope.id !== null) lines.push(`id: ${envelope.id}`);
        lines.push(`data: ${JSON.stringify(envelope.data)}`);
        const frame = decodeEndpointStreamFrame(lines, config);
        if (frame) push(frame);
      } catch (cause) { fail(cause); }
    });
    socket.addEventListener("error", () => fail(endpointStreamFailure(opened ? "WEBSOCKET_CLOSED" : "WEBSOCKET_UPGRADE_FAILED", opened ? `${config.endpoint} WebSocket transport failed` : `${config.endpoint} WebSocket upgrade failed`, config)));
    socket.addEventListener("close", () => {
      if (!signal?.aborted) fail(endpointStreamFailure(opened ? "WEBSOCKET_CLOSED" : "WEBSOCKET_UPGRADE_FAILED", opened ? `${config.endpoint} WebSocket transport closed` : `${config.endpoint} WebSocket upgrade was refused`, config));
    });
    try {
      while (!signal?.aborted) {
        const frame = await next();
        if (frame === null) break;
        yield Object.freeze({ tag: "Ready", value: frame.value });
        rememberResumeToken(key, frame.id);
      }
    } finally {
      signal?.removeEventListener("abort", abort);
      try { socket.close(1000, "stream consumer closed"); } catch {}
    }
  }

  return async function* connectEndpointStream(parameters, options = {}) {
    if (config.transport !== "websocket") {
      yield* connectEndpointSse(parameters, options);
      return;
    }
    try {
      yield* connectEndpointWebSocket(parameters, options);
    } catch (cause) {
      if (options.signal?.aborted) return;
      if (cause?.code !== "WEBSOCKET_UNAVAILABLE" && cause?.code !== "WEBSOCKET_UPGRADE_FAILED") throw cause;
      emitDevtools("realtime.transport.fallback", {
        semanticId: config.endpoint,
        payload: { from: "websocket", to: "sse", code: cause?.code ?? "WEBSOCKET_UPGRADE_FAILED", count: 1 },
      });
      const key = stableAcquisitionKey(parameters);
      yield* connectEndpointSse(parameters, { ...options, resumeToken: resumeByParameters.get(key) ?? options.resumeToken ?? null });
    }
  };
}

function normalizeStreamParameters(config, input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw streamFailure("STREAM_PARAMETERS_INVALID", `${config.id} parameters must be an object`, config);
  }
  const normalized = Object.create(null);
  for (const parameter of config.parameters) {
    if (!Object.prototype.hasOwnProperty.call(input, parameter.name)) {
      throw streamFailure("STREAM_PARAMETER_MISSING", `${config.id} is missing parameter ${parameter.name}`, config, { parameter: parameter.name });
    }
    const value = input[parameter.name];
    const valid = parameter.type === "Int"
      ? Number.isSafeInteger(value)
      : parameter.type === "String"
        ? typeof value === "string"
        : parameter.type === "Boolean"
          ? typeof value === "boolean"
          : true;
    if (!valid) {
      throw streamFailure("STREAM_PARAMETER_INVALID", `${config.id}.${parameter.name} expected ${parameter.type}`, config, {
        parameter: parameter.name,
        expected: parameter.type,
      });
    }
    normalized[parameter.name] = value;
  }
  return Object.freeze(normalized);
}

function validatePrimitiveStreamPayload(type, value, config, event) {
  const valid = type === "String"
    ? typeof value === "string"
    : type === "Int"
      ? Number.isSafeInteger(value)
      : type === "Boolean"
        ? typeof value === "boolean"
        : type === "Number" || type === "Float"
          ? typeof value === "number" && Number.isFinite(value)
          : true;
  if (!valid) {
    throw streamFailure("STREAM_EVENT_PAYLOAD_INVALID", `${config.id}.${event} expected ${type}`, config, {
      event,
      expected: type,
    });
  }
  return value;
}

function createStreamInstance(config, input, options) {
  const owner = options.owner ?? currentOwner;
  if (!owner && options.detached !== true) {
    throw streamFailure("STREAM_OWNER_REQUIRED", `${config.id} requires an Owner`, config);
  }
  if (owner?.disposed) throw streamFailure("STREAM_OWNER_DISPOSED", `${config.id} cannot attach to a disposed Owner`, config);
  const parameters = normalizeStreamParameters(config, input);
  const events = collectionSignal([]);
  const status = signal("Idle");
  const runtimeId = nextRuntimeId("stream");
  let controller = null;
  let iterator = null;
  let sequence = 0;
  let pending = [];
  let deliveryScheduled = false;
  let disposed = false;
  let terminal = false;

  function deliver() {
    deliveryScheduled = false;
    if (disposed || pending.length === 0) return;
    const batchItems = pending;
    pending = [];
    batch(() => {
      const next = events.get().slice();
      for (const envelope of batchItems) {
        while (next.length >= config.bufferCapacity) next.shift();
        next.push(envelope);
        emitDevtools("stream.event.delivered", {
          semanticId: config.id,
          runtimeId,
          ownerId: owner?.id ?? null,
          payload: { sequence: envelope.sequence, event: envelope.event.tag, buffered: next.length },
        });
      }
      events.set(next);
    });
  }

  function enqueue(event, isTerminal = false) {
    if (disposed || terminal && !isTerminal) return false;
    const buffered = events.get().length + pending.length;
    if (!isTerminal && buffered >= config.bufferCapacity) {
      if (config.backpressure === "drop_newest") {
        emitDevtools("stream.event.dropped", {
          semanticId: config.id,
          runtimeId,
          ownerId: owner?.id ?? null,
          payload: { event: event.tag, policy: config.backpressure, position: "newest" },
        });
        return false;
      }
      if (config.backpressure === "error") {
        throw streamFailure("STREAM_BACKPRESSURE_EXCEEDED", `${config.id} exceeded its buffer`, config, { event: event.tag });
      }
      emitDevtools("stream.event.dropped", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { event: event.tag, policy: config.backpressure, position: "oldest" },
      });
    }
    const envelope = Object.freeze({ sequence: ++sequence, event: Object.freeze(event) });
    pending.push(envelope);
    if (!deliveryScheduled) {
      deliveryScheduled = true;
      queueMicrotask(deliver);
    }
    return true;
  }

  function validateEvent(raw) {
    if (!raw || typeof raw !== "object" || typeof raw.tag !== "string") {
      throw streamFailure("STREAM_EVENT_INVALID", `${config.id} received an untagged event`, config);
    }
    const variant = config.variantsByName[raw.tag];
    if (!variant || variant.external !== true) {
      throw streamFailure("STREAM_EVENT_UNKNOWN", `${config.id} received unknown event ${raw.tag}`, config, { event: raw.tag });
    }
    emitDevtools("stream.validation.started", {
      semanticId: config.id,
      runtimeId,
      ownerId: owner?.id ?? null,
      payload: { boundary: config.boundary, event: raw.tag, expected: variant.payloadType },
    });
    try {
      if (variant.payloadType === null) {
        if (raw.value !== undefined) {
          throw streamFailure("STREAM_EVENT_PAYLOAD_UNEXPECTED", `${config.id}.${raw.tag} has no payload`, config, { event: raw.tag });
        }
        emitDevtools("stream.validation.succeeded", {
          semanticId: config.id,
          runtimeId,
          ownerId: owner?.id ?? null,
          payload: { boundary: config.boundary, event: raw.tag },
        });
        return { tag: raw.tag };
      }
      const value = typeof variant.validate === "function"
        ? variant.validate(raw.value)
        : validatePrimitiveStreamPayload(variant.payloadType, raw.value, config, raw.tag);
      emitDevtools("stream.validation.succeeded", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { boundary: config.boundary, event: raw.tag },
      });
      return { tag: raw.tag, value };
    } catch (error) {
      emitDevtools("stream.validation.failed", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { boundary: config.boundary, event: raw.tag, code: error?.code ?? "STREAM_EVENT_PAYLOAD_INVALID" },
      });
      throw error;
    }
  }

  async function connect() {
    if (disposed) throw streamFailure("STREAM_DISPOSED", `${config.id} instance is disposed`, config);
    if (controller) cancel("Stream connection restarted");
    const connector = options.connect ?? options.streams?.[config.id];
    if (typeof connector !== "function") {
      const error = streamFailure("STREAM_CONNECTOR_REQUIRED", `${config.id} has no connector`, config);
      status.set("Failed");
      terminal = true;
      enqueue({ tag: "Failed", value: Object.freeze({ code: error.code, message: error.message }) }, true);
      throw error;
    }
    controller = new AbortController();
    terminal = false;
    status.set("Connecting");
    emitDevtools("stream.connecting", { semanticId: config.id, runtimeId, ownerId: owner?.id ?? null });
    try {
      const source = await connector(parameters, Object.freeze({ signal: controller.signal, stream: config.id, resumeToken: options.resumeToken ?? null }));
      if (!source || typeof source[Symbol.asyncIterator] !== "function") {
        throw streamFailure("STREAM_SOURCE_INVALID", `${config.id} connector must return an AsyncIterable`, config);
      }
      iterator = source[Symbol.asyncIterator]();
      status.set("Open");
      emitDevtools("stream.connected", { semanticId: config.id, runtimeId, ownerId: owner?.id ?? null });
      while (!disposed && !controller.signal.aborted) {
        const next = await iterator.next();
        if (next.done) break;
        emitDevtools("stream.event.received", {
          semanticId: config.id,
          runtimeId,
          ownerId: owner?.id ?? null,
          payload: { event: next.value?.tag ?? null, boundary: config.boundary },
        });
        enqueue(validateEvent(next.value));
      }
      if (disposed || controller.signal.aborted) throw abortError(controller.signal.reason);
      terminal = true;
      status.set("Completed");
      enqueue({ tag: "Completed" }, true);
      emitDevtools("stream.completed", { semanticId: config.id, runtimeId, ownerId: owner?.id ?? null });
    } catch (error) {
      if (disposed || controller?.signal.aborted || error?.code === "RESOURCE_ABORTED" || error?.name === "AbortError") {
        status.set("Cancelled");
        emitDevtools("stream.cancelled", { semanticId: config.id, runtimeId, ownerId: owner?.id ?? null });
        throw abortError(controller?.signal.reason);
      }
      terminal = true;
      const failure = error instanceof StreamExecutionError
        ? error
        : streamFailure("STREAM_FAILED", `${config.id} failed`, config, { cause: error });
      status.set("Failed");
      enqueue({ tag: "Failed", value: Object.freeze({ code: failure.code, message: failure.message }) }, true);
      emitDevtools("stream.failed", {
        semanticId: config.id,
        runtimeId,
        ownerId: owner?.id ?? null,
        payload: { code: failure.code },
      });
      throw failure;
    } finally {
      iterator = null;
      controller = null;
    }
  }

  function cancel(reason = "Stream was cancelled") {
    if (controller && !controller.signal.aborted) controller.abort(reason);
    if (iterator && typeof iterator.return === "function") Promise.resolve(iterator.return()).catch(() => {});
  }

  if (options.initialSnapshot?.event) {
    const initialEvent = Object.freeze(validateEvent(options.initialSnapshot.event));
    events.set([Object.freeze({ sequence: 0, event: initialEvent })]);
    status.set("Resuming");
    emitDevtools("stream.resumed", {
      semanticId: config.id,
      runtimeId,
      ownerId: owner?.id ?? null,
      payload: { event: initialEvent.tag, hasResumeToken: options.resumeToken != null },
    });
  }
  emitDevtools("stream.created", {
    semanticId: config.id,
    runtimeId,
    ownerId: owner?.id ?? null,
    payload: { bufferCapacity: config.bufferCapacity, backpressure: config.backpressure },
  });
  if (owner) {
    onCleanup(owner, () => {
      disposed = true;
      cancel("Stream owner was disposed");
      pending = [];
      emitDevtools("stream.disposed", { semanticId: config.id, runtimeId, ownerId: owner.id });
    });
  }
  return Object.freeze({ id: config.id, runtimeId, parameters, events, status, connect, cancel });
}

export function acquireStream(
  definition,
  readParameters,
  owner = currentOwner,
  sources = null,
  options = {},
  semanticId = definition?.id ?? null,
) {
  if (!owner) throw new StreamExecutionError("STREAM_OWNER_REQUIRED", `${semanticId ?? "stream acquisition"} requires an Owner`);
  const events = collectionSignal([]);
  const status = signal("Idle");
  const runtimeId = nextRuntimeId("stream-acquisition");
  if (isScenarioStub(options, semanticId)) {
    // A finite scenario event sequence is applied by the generated harness.
    // Do not inspect parameters, construct the stream, or open a connector.
    return Object.freeze({
      id: semanticId,
      runtimeId,
      events,
      status,
      connect() { return Promise.resolve(); },
      cancel() {},
    });
  }
  let current = null;
  let disposed = false;
  let acquisitionCount = 0;
  let parameterKey = null;

  function start() {
    if (disposed) return;
    const parameters = readParameters();
    const nextParameterKey = stableAcquisitionKey(parameters);
    if (current && nextParameterKey === parameterKey) {
      emitDevtools("stream.acquisition.skipped", {
        semanticId, runtimeId, ownerId: owner.id,
        payload: { stream: definition.id, reason: "parameters_unchanged" },
      });
      return;
    }
    const reason = acquisitionCount++ === 0 ? "mount" : "arguments_changed";
    if (current) disposeOwner(current.owner);
    const initialSnapshot = options.streamSnapshots?.[semanticId] ?? null;
    events.set([]);
    status.set(initialSnapshot ? "Resuming" : "Idle");
    const streamOwner = createOwner(owner);
    const instance = definition.create(parameters, {
      ...options,
      owner: streamOwner,
      initialSnapshot,
      resumeToken: initialSnapshot?.resumeToken ?? null,
    });
    parameterKey = nextParameterKey;
    current = { owner: streamOwner, instance };
    effect(() => events.set(instance.events.get()), streamOwner, [instance.events]);
    effect(() => status.set(instance.status.get()), streamOwner, [instance.status]);
    emitDevtools("stream.acquired", {
      semanticId,
      runtimeId,
      ownerId: owner.id,
      payload: { stream: definition.id, instanceRuntimeId: instance.runtimeId, reason },
    });
    instance.connect().catch((error) => {
      if (error?.code !== "RESOURCE_ABORTED" && current?.instance === instance) {
        // The instance publishes a typed Failed terminal event.
      }
    });
  }

  effect(start, owner, sources);
  onCleanup(owner, () => {
    disposed = true;
    current = null;
    emitDevtools("stream.acquisition.disposed", { semanticId, runtimeId, ownerId: owner.id });
  });
  function active() {
    if (!current) throw new StreamExecutionError("STREAM_NOT_ACQUIRED", `${semanticId ?? definition.id} has no active stream`);
    return current.instance;
  }
  return Object.freeze({
    id: semanticId,
    runtimeId,
    events,
    status,
    connect() { return active().connect(); },
    cancel(reason) { return active().cancel(reason); },
  });
}

export function createStreamDefinition(config) {
  if (!config || typeof config !== "object" || typeof config.id !== "string") {
    throw new TypeError("Stream definition requires compiler-owned identity");
  }
  const variants = Object.freeze([...(config.variants ?? [])]);
  const variantsByName = Object.freeze(Object.fromEntries(variants.map((variant) => [variant.name, variant])));
  const normalized = Object.freeze({
    ...config,
    parameters: Object.freeze([...(config.parameters ?? [])]),
    variants,
    variantsByName,
    bufferCapacity: Number.isInteger(config.bufferCapacity) && config.bufferCapacity > 0 ? config.bufferCapacity : 64,
    backpressure: ["drop_oldest", "drop_newest", "error"].includes(config.backpressure)
      ? config.backpressure
      : "drop_oldest",
  });
  return Object.freeze({
    id: normalized.id,
    create(parameters, options = {}) { return createStreamInstance(normalized, parameters, options); },
  });
}

export class AgentExecutionError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = "AgentExecutionError";
    this.code = code;
    this.agent = details.agent ?? null;
    this.capability = details.capability ?? null;
    this.tool = details.tool ?? null;
    this.event = details.event ?? null;
    if (details.cause !== undefined) this.cause = details.cause;
  }

  toJSON() {
    return {
      code: this.code,
      agent: this.agent,
      capability: this.capability,
      tool: this.tool,
      event: this.event,
      message: this.message,
    };
  }
}

function agentFailure(code, message, config, details = {}) {
  return new AgentExecutionError(code, message, { agent: config.id, ...details });
}

function agentValueMatches(value, type) {
  if (type === null) return value === undefined;
  if (type === "AgentError") return value !== null && typeof value === "object" && !Array.isArray(value)
    && typeof value.code === "string" && typeof value.message === "string"
    && Object.keys(value).every((key) => key === "code" || key === "message");
  if (type.startsWith("Optional<") && type.endsWith(">")) {
    return value == null || agentValueMatches(value, type.slice(9, -1));
  }
  if (type.startsWith("Array<") && type.endsWith(">")) {
    return Array.isArray(value) && value.every((item) => agentValueMatches(item, type.slice(6, -1)));
  }
  if (type === "String" || type === "Date") return typeof value === "string";
  if (type === "Boolean") return typeof value === "boolean";
  if (type === "Int") return typeof value === "number" && Number.isSafeInteger(value);
  if (type === "Number" || type === "Float") return typeof value === "number" && Number.isFinite(value);
  return false;
}

function validateAgentValue(contract, value, config, role, details = {}) {
  try {
    if (typeof contract.validate === "function") return contract.validate(value);
    if (agentValueMatches(value, contract.type)) return value;
    throw agentFailure(
      "AGENT_VALIDATION_FAILED",
      `${config.id} ${role} expected ${contract.type}`,
      config,
      details,
    );
  } catch (error) {
    if (error instanceof AgentExecutionError) throw error;
    throw agentFailure(
      "AGENT_VALIDATION_FAILED",
      `${config.id} ${role} failed generated validation`,
      config,
      { ...details, cause: error },
    );
  }
}

// Engine declarations already carry their generated HTTP doors. Keep the
// default transport inside the prunable agent feature; authored client-only
// sessions can still inject invoke, and never acquire a guessed server URL.
async function invokeAgentEngine(config, input, session, options) {
  const fetcher = options.fetch ?? globalThis.fetch;
  if (typeof fetcher !== "function") throw agentFailure("AGENT_TRANSPORT_REQUIRED", "Agent HTTP transport requires fetch", config);
  const base = String(options.route?.basePath ?? options.basePath ?? "").replace(/\/$/, "");
  const path = session.resume === null ? config.engine.runPath
    : `${config.engine.resumePath}${encodeURIComponent(session.resume)}/resume`;
  const response = await fetcher(`${base}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "text/event-stream" },
    body: JSON.stringify(session.resume === null ? { input } : {}),
    signal: session.signal,
  });
  if (!response.ok) {
    await response.body?.cancel().catch(() => {});
    throw agentFailure("AGENT_HTTP_FAILED", `Agent request failed with HTTP ${response.status}; try again or check the server configuration`, config);
  }
  if (!response.headers.get("content-type")?.toLowerCase().startsWith("text/event-stream") || !response.body) {
    await response.body?.cancel().catch(() => {});
    throw agentFailure("AGENT_STREAM_INVALID", "Agent server must return a typed event stream", config);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let buffer = "";
  let bufferBytes = 0;
  let data = [];
  let frameSize = 0;
  const byteSize = (text) => new TextEncoder().encode(text).length;
  function acceptLine(line) {
    if (line.endsWith("\r")) line = line.slice(0, -1);
    if (line !== "") {
      frameSize += byteSize(line);
      if (frameSize > 262144) throw agentFailure("AGENT_STREAM_INVALID", "Agent event exceeded the 256 KiB frame limit", config);
      if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
      return null;
    }
    frameSize = 0;
    if (data.length === 0) return null;
    const raw = data.join("\n");
    data = [];
    let event;
    try { event = JSON.parse(raw); }
    catch { throw agentFailure("AGENT_STREAM_INVALID", "Agent event contained invalid JSON", config); }
    if (!event || typeof event.tag !== "string") throw agentFailure("AGENT_STREAM_INVALID", "Agent event must have a declared tag", config);
    if (event.tag === "Started") return null; // The owned session emitted it.
    if (event.tag === "Completed") return { value: event.value };
    if (event.tag === "Paused") { session.pause(event.value); return { value: null }; }
    if (event.tag === "Cancelled") throw agentFailure("AGENT_CANCELLED", "Agent run was cancelled", config);
    if (event.tag === "Failed") {
      const code = typeof event.value?.code === "string" ? event.value.code : "AGENT_RUN_FAILED";
      throw agentFailure(code, typeof event.value?.message === "string" ? event.value.message : "Agent run failed", config);
    }
    session.emit(event.tag, event.value); // Validate every domain event.
    return null;
  }
  try {
    while (true) {
      const { value, done } = await reader.read();
      const text = done ? decoder.decode() : decoder.decode(value, { stream: true });
      buffer += text;
      bufferBytes += byteSize(text);
      let newline;
      while ((newline = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, newline);
        buffer = buffer.slice(newline + 1);
        bufferBytes -= byteSize(line) + 1;
        const terminal = acceptLine(line);
        if (terminal !== null) return terminal.value;
      }
      if (bufferBytes + frameSize > 262144) throw agentFailure("AGENT_STREAM_INVALID", "Agent event exceeded the 256 KiB frame limit", config);
      if (done) throw agentFailure("AGENT_STREAM_INCOMPLETE", "Agent stream ended before a terminal event; try again", config);
    }
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

function createAgentSession(config, untrustedInput, options) {
  const owner = options.owner ?? currentOwner;
  if (!owner && options.detached !== true) {
    throw agentFailure("AGENT_OWNER_REQUIRED", `${config.id} requires an Owner`, config);
  }
  if (owner?.disposed) {
    throw agentFailure("AGENT_OWNER_DISPOSED", `${config.id} cannot attach to a disposed Owner`, config);
  }
  emitDevtools("agent.validation.started", {
    semanticId: config.id,
    ownerId: owner?.id ?? null,
    payload: { boundary: config.boundaries.input, role: "input" },
  });
  const input = validateAgentValue(config.input, untrustedInput, config, "input");
  emitDevtools("agent.validation.succeeded", {
    semanticId: config.id,
    ownerId: owner?.id ?? null,
    payload: { boundary: config.boundaries.input, role: "input" },
  });
  const events = collectionSignal([], { semanticId: `${config.id}.events`, owner });
  const status = signal("Idle", { semanticId: `${config.id}.status`, owner });
  const output = signal(null, { semanticId: `${config.id}.output`, owner });
  const runtimeId = nextRuntimeId("agent-session");
  let sequence = 0;
  let controller = null;
  let activePromise = null;
  let terminal = false;
  let disposed = false;
  let pausedRunId = null;

  function publishEvent(name, value, internal = false) {
    const variant = config.eventsByName[name];
    if (!variant) throw agentFailure("UNKNOWN_AGENT_EVENT", `${config.id} has no event ${name}`, config, { event: name });
    if (!internal && variant.external !== true) {
      throw agentFailure("AGENT_EVENT_RESERVED", `${config.id}.${name} is compiler-owned`, config, { event: name });
    }
    let trusted = value;
    if (variant.payloadType !== null) {
      emitDevtools("agent.validation.started", {
        semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
        payload: { boundary: config.boundaries.events, role: "event", event: name },
      });
      trusted = validateAgentValue(
        { type: variant.payloadType, validate: variant.validate },
        value,
        config,
        `event ${name}`,
        { event: name },
      );
      emitDevtools("agent.validation.succeeded", {
        semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
        payload: { boundary: config.boundaries.events, role: "event", event: name },
      });
    } else if (value !== undefined) {
      throw agentFailure("AGENT_EVENT_PAYLOAD_UNEXPECTED", `${config.id}.${name} has no payload`, config, { event: name });
    }
    const event = Object.freeze(
      variant.payloadType === null ? { tag: name } : { tag: name, value: trusted },
    );
    events.push(Object.freeze({ sequence: ++sequence, event }));
    emitDevtools("agent.event.delivered", {
      semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
      capture: variant.role === "message",
      payload: { event: name, role: variant.role, sequence },
    });
    return event;
  }

  async function requestPermission(capability) {
    if (config.deniedCapabilitySet.has(capability)) {
      emitDevtools("agent.capability.denied", {
        semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
        payload: { capability, reason: "explicitly_denied" },
      });
      throw agentFailure("AGENT_CAPABILITY_DENIED", `${config.id} cannot use ${capability}`, config, { capability });
    }
    if (!config.capabilitySet.has(capability)) {
      throw agentFailure("AGENT_CAPABILITY_UNDECLARED", `${config.id} did not declare ${capability}`, config, { capability });
    }
    emitDevtools("agent.capability.requested", {
      semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
      payload: { capability },
    });
    emitDevtools("agent.approval.required", {
      semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
      payload: { capability },
    });
    if (typeof options.authorize !== "function") {
      throw agentFailure("AGENT_AUTHORIZER_REQUIRED", `${config.id} requires an authorizer for ${capability}`, config, { capability });
    }
    const allowed = await options.authorize(capability, config.id);
    emitDevtools(allowed === true ? "agent.capability.granted" : "agent.capability.denied", {
      semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
      payload: { capability, reason: allowed === true ? "host_granted" : "host_denied" },
    });
    emitDevtools("agent.approval.resolved", {
      semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
      payload: { capability, approved: allowed === true },
    });
    if (allowed !== true) {
      throw agentFailure("AGENT_CAPABILITY_DENIED", `${config.id} was denied ${capability}`, config, { capability });
    }
    return true;
  }

  async function callTool(name, toolInput, capability) {
    await requestPermission(capability);
    const tool = options.tools?.[name];
    if (typeof tool !== "function") {
      throw agentFailure("AGENT_TOOL_UNAVAILABLE", `${config.id} requested unavailable tool ${name}`, config, { tool: name, capability });
    }
    emitDevtools("agent.tool.started", {
      semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
      payload: { tool: name, capability },
    });
    try {
      const result = await tool(toolInput, { signal: controller.signal, agent: config.id });
      emitDevtools("agent.tool.completed", {
        semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
        payload: { tool: name, capability },
      });
      return result;
    } catch (error) {
      emitDevtools("agent.tool.failed", {
        semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
        payload: { tool: name, capability, code: error?.code ?? null },
      });
      throw agentFailure("AGENT_TOOL_FAILED", `${config.id} tool ${name} failed`, config, { tool: name, capability, cause: error });
    }
  }

  // WO-31: an engine agent's run can stop for a deferred capability instead of
  // finishing. The transport reports it by calling `pause(runId)` from inside
  // `invoke`; the session publishes the compiler-owned `Paused(runId)` event,
  // settles as `Paused` rather than terminal, and `resume(runId)` continues the
  // same run on a new server session.
  function pause(runId) {
    if (config.engine === null) {
      throw agentFailure("AGENT_PAUSE_UNSUPPORTED", `${config.id} has no server-side engine, so it cannot pause`, config);
    }
    if (typeof runId !== "string" || runId.length === 0) {
      throw agentFailure("AGENT_PAUSE_RUN_REQUIRED", `${config.id} pause requires the run id the server reported`, config);
    }
    pausedRunId = runId;
    return publishEvent("Paused", runId, true);
  }

  async function execute(resumeRunId) {
    if (disposed) throw agentFailure("AGENT_SESSION_DISPOSED", `${config.id} session is disposed`, config);
    if (activePromise) return activePromise;
    if (terminal) throw agentFailure("AGENT_SESSION_TERMINAL", `${config.id} session has already terminated`, config);
    const invoke = options.invoke ?? options.agents?.[config.id]
      ?? (config.engine === null ? null : (input, session) => invokeAgentEngine(config, input, session, options));
    controller = new AbortController();
    pausedRunId = null;
    status.set("Running");
    publishEvent("Started", undefined, true);
    emitDevtools("agent.started", { semanticId: config.id, runtimeId, ownerId: owner?.id ?? null });
    activePromise = (async () => {
      try {
        if (typeof invoke !== "function") {
          throw agentFailure("AGENT_INVOKER_REQUIRED", `${config.id} requires an injected invoke function`, config);
        }
        const untrustedOutput = await invoke(input, {
          signal: controller.signal,
          emit(name, value) { return publishEvent(name, value, false); },
          requestPermission,
          callTool,
          pause,
          resume: resumeRunId ?? null,
        });
        if (controller.signal.aborted) {
          throw agentFailure("AGENT_CANCELLED", `${config.id} was cancelled`, config);
        }
        if (pausedRunId !== null) {
          status.set("Paused");
          emitDevtools("agent.paused", {
            semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
            payload: { runId: pausedRunId },
          });
          return null;
        }
        emitDevtools("agent.validation.started", {
          semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
          payload: { boundary: config.boundaries.output, role: "output" },
        });
        const trustedOutput = validateAgentValue(config.output, untrustedOutput, config, "output");
        emitDevtools("agent.validation.succeeded", {
          semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
          payload: { boundary: config.boundaries.output, role: "output" },
        });
        output.set(trustedOutput);
        status.set("Completed");
        terminal = true;
        publishEvent("Completed", trustedOutput, true);
        emitDevtools("agent.completed", { semanticId: config.id, runtimeId, ownerId: owner?.id ?? null });
        return trustedOutput;
      } catch (error) {
        if (controller?.signal.aborted || error?.code === "AGENT_CANCELLED" || error?.name === "AbortError") {
          if (!terminal) publishEvent("Cancelled", undefined, true);
          status.set("Cancelled");
          terminal = true;
          emitDevtools("agent.cancelled", { semanticId: config.id, runtimeId, ownerId: owner?.id ?? null });
          throw error?.code === "AGENT_CANCELLED"
            ? error
            : agentFailure("AGENT_CANCELLED", `${config.id} was cancelled`, config, { cause: error });
        }
        const failure = error instanceof AgentExecutionError
          ? error
          : agentFailure("AGENT_FAILED", `${config.id} invocation failed`, config, { cause: error });
        const publicFailure = Object.freeze({ code: failure.code, message: failure.message });
        status.set("Failed");
        terminal = true;
        publishEvent("Failed", publicFailure, true);
        emitDevtools("agent.failed", {
          semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
          payload: { code: failure.code },
        });
        throw failure;
      } finally {
        activePromise = null;
        controller = null;
      }
    })();
    return activePromise;
  }

  function start() {
    return execute(null);
  }

  function resume(runId = pausedRunId) {
    if (config.engine === null) {
      return Promise.reject(agentFailure("AGENT_RESUME_UNSUPPORTED", `${config.id} has no server-side engine, so it has no run to resume`, config));
    }
    if (typeof runId !== "string" || runId.length === 0) {
      return Promise.reject(agentFailure("AGENT_RESUME_RUN_REQUIRED", `${config.id}.resume requires the run id the Paused event carried`, config));
    }
    if (activePromise) return activePromise;
    if (status.get() !== "Paused") {
      return Promise.reject(agentFailure("AGENT_NOT_PAUSED", `${config.id} is ${status.get()}, and only a paused session resumes`, config));
    }
    return execute(runId);
  }

  function cancel(reason = "Agent session was cancelled") {
    if (controller && !controller.signal.aborted) controller.abort(reason);
  }

  emitDevtools("agent.created", {
    semanticId: config.id, runtimeId, ownerId: owner?.id ?? null,
    payload: { capabilities: config.capabilities.length, deniedCapabilities: config.deniedCapabilities.length },
  });
  if (owner) {
    onCleanup(owner, () => {
      disposed = true;
      cancel("Agent owner was disposed");
      emitDevtools("agent.disposed", { semanticId: config.id, runtimeId, ownerId: owner.id });
    });
  }
  return Object.freeze({
    id: config.id,
    runtimeId,
    input,
    events,
    status,
    output,
    start,
    resume,
    cancel,
    requestPermission,
    callTool,
  });
}

export function acquireAgent(
  definition,
  readInput,
  owner = currentOwner,
  sources = null,
  options = {},
  semanticId = definition?.id ?? null,
) {
  if (!owner) {
    throw new AgentExecutionError(
      "AGENT_OWNER_REQUIRED",
      `${semanticId ?? "agent acquisition"} requires an Owner`,
    );
  }
  if (typeof readInput !== "function") {
    throw new TypeError(`${semanticId ?? "agent acquisition"} requires an input reader`);
  }
  const events = collectionSignal([], { semanticId: `${semanticId}.events`, owner });
  const status = signal("Idle", { semanticId: `${semanticId}.status`, owner });
  const output = signal(null, { semanticId: `${semanticId}.output`, owner });
  const runtimeId = nextRuntimeId("agent-acquisition");
  let current = null;
  let disposed = false;
  let acquisitionCount = 0;
  let inputKey = null;

  function acquire() {
    if (disposed) return;
    const input = readInput();
    const nextInputKey = stableAcquisitionKey(input);
    if (current && nextInputKey === inputKey) {
      emitDevtools("agent.acquisition.skipped", {
        semanticId,
        runtimeId,
        ownerId: owner.id,
        payload: { agent: definition.id, reason: "input_unchanged" },
      });
      return;
    }
    const reason = acquisitionCount++ === 0 ? "mount" : "input_changed";
    if (current) disposeOwner(current.owner);
    events.set([]);
    status.set("Idle");
    output.set(null);
    const sessionOwner = createOwner(owner, {
      semanticId,
      label: `${definition.id} session`,
    });
    const session = definition.create(input, { ...options, owner: sessionOwner });
    inputKey = nextInputKey;
    current = { owner: sessionOwner, session };
    effect(() => events.set(session.events.get()), sessionOwner, [session.events]);
    effect(() => status.set(session.status.get()), sessionOwner, [session.status]);
    effect(() => output.set(session.output.get()), sessionOwner, [session.output]);
    emitDevtools("agent.acquired", {
      semanticId,
      runtimeId,
      ownerId: owner.id,
      payload: { agent: definition.id, sessionRuntimeId: session.runtimeId, reason },
    });
    session.start().catch((error) => {
      if (error?.code !== "AGENT_CANCELLED" && current?.session === session) {
        // The session publishes a typed Failed terminal event.
      }
    });
  }

  effect(acquire, owner, sources);
  onCleanup(owner, () => {
    disposed = true;
    current = null;
    emitDevtools("agent.acquisition.disposed", {
      semanticId,
      runtimeId,
      ownerId: owner.id,
    });
  });
  function active() {
    if (!current) {
      throw new AgentExecutionError(
        "AGENT_NOT_ACQUIRED",
        `${semanticId ?? definition.id} has no active agent session`,
        { agent: definition.id },
      );
    }
    return current.session;
  }
  return Object.freeze({
    id: semanticId,
    runtimeId,
    events,
    status,
    output,
    start() { return active().start(); },
    resume(runId) { return active().resume(runId); },
    cancel(reason) { return active().cancel(reason); },
    requestPermission(capability) { return active().requestPermission(capability); },
    callTool(name, input, capability) { return active().callTool(name, input, capability); },
  });
}

export function createAgentDefinition(config) {
  if (!config || typeof config !== "object" || typeof config.id !== "string") {
    throw new TypeError("Agent definition requires compiler-owned identity");
  }
  if (!config.input || !config.output || !Array.isArray(config.events)) {
    throw new TypeError("Agent definition requires input, output, and events");
  }
  const events = Object.freeze([...config.events]);
  const normalized = Object.freeze({
    ...config,
    engine: config.engine ?? null,
    events,
    eventsByName: Object.freeze(Object.fromEntries(events.map((event) => [event.name, event]))),
    capabilities: Object.freeze([...(config.capabilities ?? [])]),
    deniedCapabilities: Object.freeze([...(config.deniedCapabilities ?? [])]),
    capabilitySet: new Set(config.capabilities ?? []),
    deniedCapabilitySet: new Set(config.deniedCapabilities ?? []),
  });
  return Object.freeze({
    id: normalized.id,
    engine: normalized.engine,
    capabilities: normalized.capabilities,
    deniedCapabilities: normalized.deniedCapabilities,
    events,
    create(input, options = {}) { return createAgentSession(normalized, input, options); },
  });
}



